import {
    Alignment, ArrayUtil, BinarySearch, Collection, Color, DebouncedFunction, DOMUtil, FieldUpdateEvent, getThemeColor,
    getThemeForKey, HeightWidth, HorizontalAlignment, Keys, LeftOrRightAlignment, LocalUserSettings, MixinUtil, Model, ModelRow, Mutex,
    PermissionsUtil, SortDirection, SortUtil, StringUtil, UserSettings, VerticalAlignment
} from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import {
    AllowedUse, Button, Container, ContextMenuLabelProps, CrudDecoratorCloseEvent, CrudDecoratorCloseListener,
    DataSourceExecutionEvent, DesignableObjectCreationMethod, DomEvent, DomMouseEvent, Image, KeyHandler, Label, Layout,
    MainPageAccessor, OpenType, Panel, PanelProps, serializeComponents, TableAddRowOptions, TableAddRowResult,
    TableRowAdditionalToolsDisplayEvent, TableRowAdditionalToolsDisplayListener, TableRowModeChangeEvent,
    TableRowModeChangeListener, Textbox, ToolsTableCell, ValidationResult
} from "../..";
import { AdditionalTableRowToolsDesignerPanel } from "./AdditionalTableRowToolsDesignerPanel";
import { AddType } from "./AddType";
import { BusyMixin } from "../../mixins/BusyMixin";
import { BusyMixinInterface } from "../../mixins/BusyMixinInterface";
import { ClearButtonVisible } from "../textbox/ClearButtonVisible";
import { ClickEvent } from "../../events/ClickEvent";
import { CloneComponent } from "../../base/CloneComponent";
import { Component } from "../../base/Component";
import { ComponentCreator, ComponentFactory } from "../../page/ComponentFactory";
import { ComponentDeserializer, DeserializeProps } from "../../serializer/ComponentDeserializer";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { ComponentSearcher } from "./ComponentSearcher";
import { ComponentTypes } from "../../base/ComponentTypes";
import { CrudDecoratorPermissionsType } from "../../page/decorators/CrudDecoratorPermissionsType";
import { DataSource, DataSourceAction, DataSourceMode } from "../../databinding/DataSource";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { DesignerInterface } from "../../base/DesignerInterface";
import { DesignerToolsHandler } from "./DesignerToolsHandler";
import { DesignerToolsTableColumn } from "./DesignerToolsTableColumn";
import { DropTargetPanel } from "../panel/DropTargetPanel";
import { EditRowDecorator } from "../../page/decorators/EditRowDecorator";
import { ExpandHandler } from "./ExpandHandler";
import { ExpandTableColumn } from "./ExpandTableColumn";
import { FieldSortInfo } from "./FieldSortInfo";
import { GeneratedTableColumn } from "./GeneratedTableColumn";
import { KeyEvent } from "../../events/KeyEvent";
import { ListenerListDef } from "../../base/ListenerListDef";
import { MountCallback, MountUtil } from "../../base/MountListener";
import { Overlay } from "../../page/Overlay";
import { PermissionsDefinition } from "../../base/PermissionsDefinition";
import { QuickAddRowPosition } from "./QuickAddRowPosition";
import { ReflectiveDialogs } from "../../base/ReflectiveDialogs";
import { RowModeControlType } from "./RowModeControlType";
import { SearchFilterMode, FilterAlignment } from "./headingPanel/searchFilter/SearchFilterPanel";
import { SelectCheckboxHandler } from "./SelectCheckboxHandler";
import { SelectCheckboxTableColumn } from "./SelectCheckboxTableColumn";
import { SelectionMode, SelectionType } from "../../base/SelectionMode";
import { serializeProps } from "../../serializer/ComponentSerializer";
import { SortFieldInfo } from "./SortFieldInfo";
import { SortSelector } from "./SortSelector";
import { Tab } from "../tabset/Tab";
import {
    TableAction, TableContentsChangedEvent, TableContentsChangedListener
} from "../../events/TableContentsChangedEvent";
import { TableCell } from "./TableCell";
import { TableColumn } from "./TableColumn";
import { TableConfig } from "./TableConfig";
import { TableCrudManager } from "./TableCrudManager";
import { TableFilterChangedEvent, TableFilterChangedListener } from "../../events/TableFilterChangedEvent";
import { TableHeadingPanel } from "./headingPanel/TableHeadingPanel";
import { TableHeadingRow } from "./TableHeadingRow";
import { TablePropDefinitions, TableProps, TableRowEditResolveMode } from "./TableProps";
import { TableRow, DragSession } from "./TableRow";
import { TableRowBeforeSaveEvent, TableRowBeforeSaveListener } from "../../events/TableRowBeforeSaveEvent";
import { TableRowCreationEvent, TableRowCreationListener } from "../../events/TableRowCreationEvent";
import { TableRowDisplayEvent, TableRowDisplayListener } from "../../events/TableRowDisplayEvent";
import { TableRowExpansionEvent, TableRowExpansionListener } from "../../events/TableRowExpansionEvent";
import { TableRowMode } from "./TableRowMode";
import { TableRowMoveEvent, TableRowMoveListener } from "../../events/TableRowMoveEvent";
import { TableRowProps } from "./TableRowProps";
import { TableRowStringSearcher } from "./TableRowStringSearcher";
import { TableSelectionEvent, TableSelectionListener } from "../../events/TableSelectionEvent";
import { TableStyles } from "./TableStyles";
import { ToolsHandler } from "./ToolsHandler";
import { ToolsHeadingTableCell } from "./ToolsHeadingTableCell";
import { ToolsTableColumn } from "./ToolsTableColumn";
import { HeadingTableCellResizer } from "./HeadingTableCellResizer";

const defaultExpandBackground = "background4";
const defaultExpandColor = "primary.light";

const _rowExpandListenerDef: ListenerListDef = { listName: "_rowExpandListeners" };
const _rowCollapseListenerDef: ListenerListDef = { listName: "_rowCollapseListeners" };
const _rowCreateListenerDef: ListenerListDef = { listName: "_rowCreateListeners" };
const _rowBeforeSaveListenerDef: ListenerListDef = { listName: "_rowBeforeSaveListeners" };
const _contentsChangedListenerDef: ListenerListDef = { listName: "_contentsChangedListenerDef" };
const _filterChangedListenerDef: ListenerListDef = { listName: "_filterChangedListenerDef" };
const _afterTableRowMoveListenerDef: ListenerListDef = { listName: "_afterTableRowMoveListenerDef" };
const _beforeTableRowMoveListenerDef: ListenerListDef = { listName: "_beforeTableRowMoveListenerDef" };
const _afterCrudCoseListenerDef: ListenerListDef = { listName: "_afterCrudCoseListenerDef" };
const _rowDisplayListenerDef: ListenerListDef = { listName: "_rowDisplayListeners" };
const _rowModeChangeListenerDef: ListenerListDef = { listName: "_rowModeChangeListeners" };
const _rowAdditionalToolsListenersDef: ListenerListDef = { listName: "_rowAdditionalToolsListeners" };
const _selectionListenerDef: ListenerListDef = { listName: "_selectionListeners" };

const log = DesignableObjectLogManager.getLogger("components.table.Table");

enum InnerIds {
    HEADING_PANEL = "headingPanel"
}

export type ExpandComponentSource = Component | ((row: TableRow) => Component) | any;

export class Table extends Component implements TableProps {
    private _allowAddDisabledByServer: boolean;
    private _allowAdvancedSearch: boolean;
    private _allowConfig: boolean;
    private _allowDelete: boolean;
    private _allowDeleteDisabledByServer: boolean;
    protected _allowDetail: boolean;
    private _allowEdit: boolean;
    private _allowEditDisabledByServer: boolean;
    private _allowExport: boolean;
    private _allowFilter: boolean;
    private _allowPin: boolean;
    private _allowShare: boolean;
    public _columns: TableColumn[];
    private _crudManager: TableCrudManager;
    private debouncedApplyFilters = DebouncedFunction.create(() => this.applyFilters(), 300);
    private debouncedCheckHeadingPanelToolAvailability =
        DebouncedFunction.create(() => this.headingPanel.checkToolAvailability(), 300);
    private hasDisplayedData: boolean = false;
    public rowSpacing: number;
    private _rows: TableRow[];
    private _allRows: TableRow[];
    private _selectedIndexes: number[];
    public virtualized: boolean;
    private _table: HTMLTableElement;
    protected _tbody: HTMLElement;
    protected _thead: HTMLTableSectionElement;
    private lastScrollLeft: number;
    private headingRow: TableHeadingRow;
    private _observer: IntersectionObserver;
    private _columnHeadingsVisible: boolean;
    private _columnsResizable: boolean;
    private _headerVisible: boolean;

    // Expand Column/Component
    private _expandCellAlignment: LeftOrRightAlignment;
    private _expandComponentSource: ExpandComponentSource;
    private _expandHandler: ExpandHandler;
    private expandRowsPromise: Promise<boolean>;

    // Select Checkbox column
    private _displaySelectCheckbox: boolean;
    private _selectCheckboxAlignment: LeftOrRightAlignment;
    private _selectCheckboxHandler: SelectCheckboxHandler;

    // Tools Column
    private _displayToolsCell: boolean;
    private _toolsCellAlignment: LeftOrRightAlignment;
    private _toolsHandler: ToolsHandler;
    private _largestToolsCellWidth: number = 0;

    // Designer Tools Column
    private _designerToolsHandler: DesignerToolsHandler;

    private _selectionMode: SelectionMode;
    private fixedRowHeightWidth: HeightWidth;
    private _data: any[] = [];
    private _filteredRows: TableRow[];
    private _searchFilterMode: SearchFilterMode;
    private _filterAlignment: FilterAlignment;
    private _filterValue: string;
    private _addlSearcherCallback: () => ComponentSearcher[];
    private _headingPanel: TableHeadingPanel;
    private _tableRowStringSearcher: TableRowStringSearcher;
    private _filterClearButtonVisible: ClearButtonVisible;
    private _busyImage: Image;
    private _panelDesignerExpansionDropTarget: Panel;
    private _additionalRowToolsDef: any;
    private _additionalRowToolsPanel: AdditionalTableRowToolsDesignerPanel;
    private _emptyCaptionAfterSearch: string;
    private _emptyCaptionBeforeSearch: string;
    private _emptyComponentAfterSearch: Component;
    private _emptyComponentBeforeSearch: Component;
    private _noRecordsMatch: boolean;
    private _sortInfo: FieldSortInfo[];
    private _addType: AddType;
    private quickAddRowMutex = new Mutex();
    private _quickAddRow: TableRow;
    private _quickAddData: ModelRow | any;
    private _quickAddRowPosition: QuickAddRowPosition;
    private _rowModeControlType: RowModeControlType;
    private _rowProps: Partial<TableRowProps>;
    private _allowDbSearch: boolean = false;
    private _columnSortOverlay: Overlay;
    public rowAlign: VerticalAlignment;
    public rowBorderBottomWidth: number;
    public rowBorderBottomColor: Color;
    public rowBorderTopWidth: number;
    public rowBorderTopColor: Color;
    public _generalLayout: string;
    public _detailLayout: string;
    public _editLayout: string;
    public _addLayout: string;
    public _searchLayout: string;
    public editLayoutWidth: number;
    public crudDecoratorWidth: string | number;
    public exportName: string;
    private _resizeObserver: ResizeObserver;

    public possibleConfigs: Collection<string> = {};
    private _configInUse: TableConfig;
    private _baseConfig: TableConfig;
    private _sequenceField: string;
    private deactivateOutsideClickListener: () => void;
    private _insideClickEvent: PointerEvent;
    private _rowEditResolveMode: TableRowEditResolveMode;
    private _persistChangesImmediately: boolean;
    public onEditLayoutLoaded: (rowDecorator: EditRowDecorator, tableRow: TableRow) => void;
    public onValidate: (table: Table) => ValidationResult[];
    private adhocTopRows: TableRow[];
    public _printableToggleEnabled: boolean;
    private _oddRowColor: Color;
    private _evenRowColor: Color;
    private _ignoreRowColorSetting: boolean;
    private _customFilter: (data:TableRow) => boolean;
    private removePlaceholderRowDisplayListener = false;
    private _displayingInViewMode: boolean;
    public validateRowDetail: (row: TableRow) => string;

    constructor(props?: Partial<TableProps>) {
        super("div", props);
        this._element.classList.add(TableStyles.base);
        this._columns = [];
        this._rows = [];
        this._allRows = []; //this array contains all the table's rows, even the ones that aren't displayed
        this._selectedIndexes = [];
        this.rowSpacing = 4;
        this.virtualized = true; // props == null || props.virtualized === undefined ? true : props.virtualized;
        this._table = document.createElement("table");
        this._table.className = TableStyles.tableBase;
        this._tbody = document.createElement("tbody");
        this._tbody.className = TableStyles.tbodyBase;
        this._tbody.addEventListener("scroll", (event: DomEvent) => this.bodyScrolled(event));
        this._tbody.tabIndex = 0;
        this._thead = document.createElement("thead");
        this._thead.className = TableStyles.theadBase + " " + TableStyles.hideScroll;
        this.headingRow = new TableHeadingRow(this);
        this._thead.appendChild(this.headingRow._element);
        this._thead.addEventListener("scroll", (event: DomEvent) => this.headScrolled(event));
        this._table.appendChild(this._thead);
        this._table.appendChild(this._tbody);
        this.addHeadingPanel();
        this._element.appendChild(this._table);
        if (this.virtualized === true) {
            this.addRowDisplayListener(this.setPlaceholderRowDimensions);
            this._observer = this.virtualizedIntersectionObserver;
        }
        this._columnHeadingsVisible = true;
        this._headerVisible = true;
        this._addType = AddType.NOT_ALLOWED;
        this._rowModeControlType = RowModeControlType.AUTO;
        this._rowProps = null;

        this.setProps(props);

        if (props?.["_creationMethod"] === DesignableObjectCreationMethod.DROPPING_IN_DESIGNER) {
            const emptyColumn = this._createNewColumnForDesigner();
            this.addColumn(emptyColumn);
        }

        // Need to ensure that toolsHandler has a chance to create...because displayToolsCell defaults to 'true', it may
        // not be set as part of table creation.
        this.syncToolsHandler();

        this.ensureEmptyComponentsCreated();
        this.addKeyDownListener((event: KeyEvent) => this.sendKey(event));
        this.syncQuickAddRow();
        this._resizeObserver = new ResizeObserver(entries => {
            window.requestAnimationFrame(() => {
                if (Array.isArray(entries) && entries.length > 0)
                    this.tableResized();
            });
        });
        this._resizeObserver.observe(this._tbody);
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.headingPanel, InnerIds.HEADING_PANEL);
    }

    protected get virtualizedIntersectionObserver(): IntersectionObserver {
        return new IntersectionObserver((entries) => this.handleIntersection(entries), { root: this._tbody });
    }

    public override async ensureLoaded() {
        await this.headingPanel?.ensureLoaded();
        if (this.designerRow != null) {
            await this.designerRow.populateDOMIfNeeded();
        }
    }

    activateValidateOnOutsideClick(insideClickEvent: PointerEvent) {
        this._insideClickEvent = insideClickEvent;
        if (this.deactivateOutsideClickListener == null && this.allowsInlineEditing) {
            const excludedContainers = new Set([MainPageAccessor.mainPage, ...MainPageAccessor.mainPage.components]);
            const topMostParent = this.findTopMostParent(p => !excludedContainers.has(p));
            if (topMostParent == null) {
                return;
            }

            log.debug(this, "Activating outside click listener");
            topMostParent._element.addEventListener("click", this.outsideClickListener, true);

            this.deactivateOutsideClickListener = () => {
                log.debug(this, "Deactivating outside click listener");
                topMostParent._element.removeEventListener("click", this.outsideClickListener, true);
                this._insideClickEvent = null;
                this.deactivateOutsideClickListener = null;
            };
        }
    }

    private outsideClickListener = (event) => {
        if (!this.isMounted) {
            this.clearSelectedRows();
            this.deactivateOutsideClickListener();
            return;
        }

        if (this._insideClickEvent === event ||
            this._tbody.contains(event.target)) {
            return;
        }

        const clickedInsideHeadingPanel =
            this.headerVisible &&
            DOMUtil.isOrContains(this.headingPanel._element, event.target);

        if (clickedInsideHeadingPanel) {
            this.completeEditedRows();
            return;
        }

        if (this.completeEditedRows()) {
            this.clearSelectedRows();
        }
        this.deactivateOutsideClickListener();
    }

    /**
     * Callback that defines things to do when the table is resized
     */
    private tableResized() {
        this.resetHeadingRowPaddingForScrollbar();
        this.recalculateVirtualPlaceholders();
    }

    setProps(props: Partial<TableProps>) {
        super.setProps(props);
    }

    private addHeadingPanel() {
        this.createHeadingPanel();
        this.headingPanel.id = this.createInnerComponentId(InnerIds.HEADING_PANEL);
        this._element.appendChild(this.headingPanel._element);
    }

    /**
     * Only present for so mobile components can override it
     */
    protected createHeadingPanel() {
        this.headingPanel = new TableHeadingPanel(this);
    }

    /**
     * Only present for mobile components
     */
    get headingPanel(): TableHeadingPanel {
        return this._headingPanel;
    }

    /**
     * Only present for mobile components
     */
    set headingPanel(value: TableHeadingPanel) {
        this._headingPanel = value;
    }

    /**
     * Only present for mobile components
     */
    public get textboxSearch(): Textbox {
        return this.headingPanel?.textboxSearch;
    }

    public get textboxFilter(): Textbox {
        return this.headingPanel?.textboxFilter;
    }

    /**
     * Only present for mobile components
     */
    protected get labelRowCount(): Label {
        return this.headingPanel?.labelRowCount;
    }

    get id(): string {
        return super.id;
    }

    set id(value: string) {
        const oldId = this.id;
        const oldExpansionId = this.expandComponentSource?.id;
        super.id = value;
        if (this._tbody != null)
            this._tbody.id = value + "-body";
        if (this.expandComponentSource != null &&
            (StringUtil.isEmptyString(this.expandComponentSource.id) === true || oldExpansionId === (oldId + "-expand"))) {
            this.expandComponentSource.id = this.getExpandComponentId();
        }
        this.applyDefaultConfig();
    }

    get crudManager(): TableCrudManager {
        if (this._crudManager == null) {
            this._crudManager = this.createCrudManager();
        }
        return this._crudManager;
    }

    /**
     * Only present for mobile components
     */
    protected createCrudManager(): TableCrudManager {
        return new TableCrudManager(this);
    }

    get selectionMode(): SelectionMode {
        return this._selectionMode || SelectionMode.NONE;
    }

    set selectionMode(value: SelectionMode) {
        this._selectionMode = value;
        this.syncSelectCheckboxHandler();
    }

    get selectedIndexes(): number[] {
        return this._selectedIndexes;
    }

    set selectedIndexes(value: number[]) {
        this._setSelectedIndexesInternal(value, null);
    }

    get rowProps(): Partial<TableRowProps> {
        return this._rowProps;
    }

    set rowProps(value: Partial<TableRowProps>) {
        const initialRowProps = this._rowProps;
        this._rowProps = value;

        if (initialRowProps == null) {
            this.designerRow?.setProps({ ...this._rowProps });
        }
    }

    get tableHeadingRow(): TableHeadingRow {
        return this.headingRow;
    }

    public set addButtonCaption(value: string) {
        this.headingPanel.addButtonCaption = value;
    }

    get allowsInlineEditing(): boolean {
        return this.allowEdit && this.rowModeControlType === RowModeControlType.AUTO;
    }

    private _setSelectedIndexesInternal(value: number[], domEvent: DomEvent) {
        const oldIndexes = this.selectedIndexes;
        if (value == null)
            value = [];

        if (ArrayUtil.equals(value, oldIndexes)) {
            return;
        }

        // If selecting multiple rows or unselecting a row on a table that allows inline editing, complete row edits if needed.
        if (value.length !== 1  && this.allowsInlineEditing && this._getRowModeFromDataSource() !== TableRowMode.SEARCH) {
            //If a row being edited cannot be completed (due to an error), cancel the row selection.
            if (this.completeEditedRows() !== true) {
                log.debug("Cancelling row selection, row being edited in error");
                this.selectCheckboxHandler?.syncAllCheckboxes();
                this.selectedRow?.findNonSelectCheckboxFocusableComponent()?.focus();
                return;
            }
        }
        for (const index of this.selectedIndexes) {
            const previouslySelectedRow = this.rows[index];
            // The previously selected row may have been removed already
            if (previouslySelectedRow != null)
                previouslySelectedRow.selected = false;
        }
        this._selectedIndexes = value;
        for (const index of value)
            this.rows[index].selected = true;
        this.headingPanel.checkToolAvailability();
        const event = new TableSelectionEvent(this, oldIndexes, this.getRowsByIndexes(oldIndexes), this.selectedIndexes,
            this.getRowsByIndexes(value), domEvent);
        this.fireSelectionListeners(event);
    }

    getRowsByIndexes(indexes: number[]): TableRow[] {
        const result: TableRow[] = [];
        for (const index of indexes)
            result.push(this.rows[index]);
        return result;
    }

    getRowByIndex(index: number): TableRow {
        if (index < 0 || index >= this.rows.length)
            return null;
        return this.rows[index];
    }

    getRowByDataKeyValue(keyValue: any): TableRow {
        const keyField = this.dataSource?.getMetadataFromCache()?.keyFields[0];
        if (keyField == null) {
            return null;
        }
        return this.rows.find(row => row.data?.get(keyField) === keyValue);
    }

    /**
     * The rows array contains the list of rows that are currently displayed.  I wish it was called displayedRows instead.
     */
    get rows() {
        return this._rows;
    }

    set rows(value: TableRow[]) {
        this._rows = value;
    }

    /**
     * Gets all TableRows whether they are filtered out / displayed or not.
     */
    get allRows() {
        return this._allRows;
    }

    get hasSelection(): boolean {
        return ArrayUtil.isEmptyArray(this.selectedIndexes) === false;
    }

    get selectedIndex() {
        if (this.selectedIndexes.length !== 1)
            return -1;
        return this.selectedIndexes[0];
    }

    set selectedIndex(value) {
        if (this.filteredRows == null || value >= this.filteredRows.length)
            this.selectedIndexes = [];
        else
            this.selectedIndexes = [value];
    }

    get selectedAllRowsIndex(): number {
        const selectedRow = this.selectedRow;
        if (selectedRow == null)
            return -1;
        return this._allRows.indexOf(selectedRow);
    }

    clearSelectedRows(event?: DomEvent) {
        this._setSelectedRowsInternal([], event);
    }

    get selectedRows(): TableRow[] {
        const result = [];
        for (const index of this.selectedIndexes)
            result.push(this.rows[index]);
        return result;
    }

    set selectedRows(value: TableRow[]) {
        this._setSelectedRowsInternal(value, null);
    }

    public getSelectedRowsMatching(filter: (tableRow: TableRow) => boolean): TableRow[] {
        return this.selectedRows.filter(filter);
    }

    private _setSelectedRowsInternal(value: TableRow[], event: DomEvent) {
        if (value == null) {
            value = [];
        }
        const indexes = this.buildSelectedIndexesArray(value);
        this._setSelectedIndexesInternal(indexes, event);
    }

    private buildSelectedIndexesArray(selectedRows: TableRow[]): number[] {
        const indexes = [];
        for (const row of selectedRows) {
            if (this.indexOf(row) > -1) {
                indexes.push(this.indexOf(row));
            }
        }
        return indexes;
    }

    get selectedRow(): TableRow {
        const index = this.selectedIndex;
        if (index < 0)
            return null;
        return this.rows[index];
    }

    set selectedRow(value: TableRow) {
        const index = this.indexOf(value);
        if (index < 0)
            this.selectedIndexes = [];
        else
            this.selectedIndexes = [index];
    }

    get columnHeadingsVisible() {
        return this._columnHeadingsVisible;
    }

    set columnHeadingsVisible(value) {
        if (value === this._columnHeadingsVisible)
            return;
        this._columnHeadingsVisible = value;
        if (value)
            this._table.insertBefore(this._thead, this._tbody);
        else
            this._table.removeChild(this._thead);
    }

    get columnsResizable() {
        return this._columnsResizable ?? this.getPropertyDefinitions().columnsResizable.defaultValue;
    }

    set columnsResizable(value: boolean) {
        if (this._columnsResizable === value)
            return;
        this._columnsResizable = value;
        this.syncColumnResizers();
    }

    public syncColumnResizers() {
        this.columns.forEach(c => {
            // Despite the type, sometimes this.columns is an array of property objects.
            if (c instanceof TableColumn)
                c.syncColumnResizer();
        });
    }

    get columns() {
        return this._columns;
    }

    set columns(value: TableColumn[]) {
        this._columns = [];
        if (value != null) {
            for (let x = 0; x < value.length; x++) {
                const column = value[x];
                this.addColumn(column);
                if (this.owner != null && column.id != null)
                    this.owner[column.id] = column;
            }
        }
        this.toolsHandler?.syncColumnPlacement();
        this.selectCheckboxHandler?.syncColumnPlacement();
        this.expandHandler?.syncColumnPlacement();
    }

    private getNormalColumns(): TableColumn[] {
        return this.columns.filter(column => {
            return (!(column instanceof DesignerToolsTableColumn)) &&
                (!(column instanceof ExpandTableColumn)) &&
                (!(column instanceof ToolsTableColumn)) &&
                (!(column instanceof SelectCheckboxTableColumn));
        });
    }

    adhocScroll(options?: ScrollToOptions) {
        this._tbody.scrollTo(options);
    }

    scrollToSelection() {
        if (this.selectedRows.length > 0)
            this.selectedRows[0].scrollIntoView();
    }

    sendKey(event: KeyEvent | KeyboardEvent): boolean {
        if (event.ctrlKey && event.key === Keys.A.toLowerCase() && this.selectionMode === SelectionMode.MULTI) {
            this.selectedIndexes = this.rows.reduce((result, row, index) => {
                if (row.enabled) result.push(index);
                return result;
            }, []);
            event.preventDefault();
            return true;
        }
        if (event.ctrlKey || event.altKey || this.selectionMode === SelectionMode.NONE)
            return false;
        if (event.key === Keys.ARROW_UP || event.key === Keys.ARROW_DOWN || event.key === Keys.ARROW_LEFT || event.key === Keys.ARROW_RIGHT) {
            if (this.selectedRows.length !== 1) {
                if (this.rows.length > 0) {
                    this.selectedRows = [this.rows[0]];
                    this.scrollToSelection();
                }
            }
            else {
                let index = this.rows.indexOf(this.selectedRows[0]);
                if (index < 0)
                    index = 0;
                if (event.key === Keys.ARROW_DOWN) {
                    if (index < this.rows.length - 1) {
                        this.selectedRows = [this.rows[index + 1]];
                        this.scrollToSelection();
                    }
                }
                else if (event.key === Keys.ARROW_UP) {
                    if (index > 0) {
                        this.selectedRows = [this.rows[index - 1]];
                        this.scrollToSelection();
                    }
                }
                else if (event.key === Keys.ARROW_RIGHT && this.selectedRows[0].expandable)
                    this.selectedRows[0].expanded = true;
                else if (event.key === Keys.ARROW_LEFT && this.selectedRows[0].expandable)
                    this.selectedRows[0].expanded = false;
            }
            event.preventDefault();
            return true;
        }
        return false;
    }

    handleIntersection(entries) {
        for (let i = 0; i < entries.length; i++) {
            const target = entries[i].target;
            if (entries[i].isIntersecting) {
                target.__row.populateDOMIfNeeded();
                this._observer.unobserve(target);
            }
        }
    }

    public addColumn(column: TableColumn) {
        if (column instanceof GeneratedTableColumn) {
            log.debug("Attempted to manually add a generated/special column, will not add");
            return;
        }
        this.internalAddColumn(column, (column: TableColumn) => this.insertNormalColumn(column));
    }

    /**
     * Add a special/generated column at the end.  These columns are always repositioned after they are added.
     */
    _addGeneratedColumn(column: TableColumn) {
        if (!(column instanceof GeneratedTableColumn)) {
            log.debug("Attempted to manually add a normal column via the generated/special column logic, will not add");
            return;
        }
        const addAsLast = (column: TableColumn) => this.columns.push(column);
        this.internalAddColumn(column, addAsLast);
    }

    private internalAddColumn(column: TableColumn, insertionFunction: (column: TableColumn) => void) {
        insertionFunction(column);
        this.resetColumnIndexes();
        column.table = this;
        const headingCell = column.headingCell;
        this.headingRow.addCell(headingCell, this._columns.indexOf(column));
        this.setupColumnSorting(column);
        column.matchDefaultColumnAlignment();
    }

    override doAfterDeserialize() {
        this.addDesignerRow();
    }

    /**
     * Inserts the provided column at the proper index based on any generated table columns that may be present (such as
     * the expand cell, the tools cell, etc.).
     */
    private insertNormalColumn(column: TableColumn) {
        let offset = 0;
        if (this.toolsCellAlignment === Alignment.RIGHT) {
            if (this.columns.find(column => column instanceof ToolsTableColumn) != null) {
                offset++;
            }
        }
        if (this.expandCellAlignment === Alignment.RIGHT) {
            if (this.columns.find(column => column instanceof ExpandTableColumn) != null) {
                offset++;
            }
        }
        if (this.selectCheckboxAlignment === Alignment.RIGHT) {
            if (this.columns.find(column => column instanceof SelectCheckboxTableColumn) != null) {
                offset++;
            }
        }
        if (this.columns.find(column => column instanceof DesignerToolsTableColumn) != null) {
            offset++;
        }
        const finalIndex = Math.max(0, (this.columns.length - offset));
        this.columns.splice(finalIndex, 0, column);
    }

    private async setupColumnSorting(column: TableColumn) {
        let sortSelector: SortSelector;
        const fields: SortFieldInfo[] = await column.getSortFields();
        if (this._designer == null) {
            if (fields.length === 1) {
                sortSelector = new SortSelector(fields[0], { text: column.headingCell.caption, tooltip: column.headingCell.tooltip });
                sortSelector.addClickListener((event: ClickEvent) => this.sortFromClick(event));
            }
            else if (fields.length > 1) {
                sortSelector = new SortSelector(null, { text: column.headingCell.caption, tooltip: column.headingCell.tooltip });
                const sortPanel = this.createSortPanel(sortSelector, fields);
                sortSelector.addClickListener(() => {
                    Overlay.alignToAnchor(sortPanel, sortSelector);
                    this.updateSortDisplay();
                    this._columnSortOverlay = Overlay.showInOverlay(sortPanel);
                });
            }
            else if (StringUtil.isEmptyString(column.headingCell.caption) === false)
                sortSelector = new SortSelector(null, { text: column.headingCell.caption });
        }
        else if (StringUtil.isEmptyString(column.headingCell.caption) === false)
            sortSelector = new SortSelector(fields[0], { text: column.headingCell.caption });
        column.headingCell.forEveryTopLevelChildComponentReverse(component => {
            if (component instanceof SortSelector)
                column.headingCell.remove(component);
        });
        if (sortSelector != null)
            column.headingCell.insert(sortSelector, 0);
    }

    private createSortPanel(sortSelector: SortSelector, fields: SortFieldInfo[]): Panel {
        const sortPanel = new Panel({ id: "sortPanel", minWidth: 140 });
        for (const field of fields) {
            let fieldCaption = field.caption;
            if (fieldCaption == null) {
                const metadata = this.dataSource?.getMetadataFromCache();
                const outputField = metadata?.getFieldFromOutput(field.field);
                fieldCaption = outputField?.caption;
            }
            if (fieldCaption == null)
                fieldCaption = field.field;
            const fieldLabel = new SortSelector(field, { id: field.field, text: fieldCaption, allowSelect: false, fontBold: true, fontSize: "small", marginTop: 5 }, sortSelector);
            fieldLabel.addClickListener(event => {
                Overlay.hideOverlay(this._columnSortOverlay);
                this.sortFromClick(event, sortSelector);
            });
            sortPanel.add(fieldLabel);
        }
        return sortPanel;
    }

    public async resetColumnSorting() {
        for (const column of this.columns) {
            await this.setupColumnSorting(column);
        }
        this.syncColumnResizers();
    }

    private forEverySortSelector(callback: (selector: SortSelector) => void) {
        for (const column of this.columns) {
            for (const headingComp of column.headingCell.components) {
                if (headingComp instanceof SortSelector)
                    callback(headingComp);
            }
        }
    }

    get displaySelectCheckbox(): boolean {
        return (this._displaySelectCheckbox ?? this.getPropertyDefinitions().displaySelectCheckbox.defaultValue) &&
            this.selectionMode === SelectionMode.MULTI;
    }

    set displaySelectCheckbox(value: boolean) {
        this._displaySelectCheckbox = value;
        this.syncSelectCheckboxHandler();
    }

    get selectCheckboxAlignment(): LeftOrRightAlignment {
        return this._selectCheckboxAlignment ?? this.getPropertyDefinitions().selectCheckboxAlignment.defaultValue;
    }

    set selectCheckboxAlignment(value: LeftOrRightAlignment) {
        this._selectCheckboxAlignment = value;
        this.syncSelectCheckboxHandler();
    }

    get selectCheckboxHandler(): SelectCheckboxHandler {
        return this._selectCheckboxHandler;
    }

    private set selectCheckboxHandler(value: SelectCheckboxHandler) {
        this._selectCheckboxHandler = value;
    }

    private syncSelectCheckboxHandler() {
        if (this.displaySelectCheckbox !== true) {
            this.selectCheckboxHandler?.removeColumn();
            this.selectCheckboxHandler = null;
        }
        else {
            if (this.selectCheckboxHandler == null) {
                this.selectCheckboxHandler = new SelectCheckboxHandler(this);
            }
            this.selectCheckboxHandler.syncColumnPlacement();
        }
    }

    get displayToolsCell(): boolean {
        return this._displayToolsCell ?? this.getPropertyDefinitions().displayToolsCell.defaultValue;
    }

    set displayToolsCell(value: boolean) {
        this._displayToolsCell = value;
        this.syncToolsHandler();
    }

    get toolsCellAlignment(): LeftOrRightAlignment {
        return this._toolsCellAlignment ?? this.getPropertyDefinitions().toolsCellAlignment.defaultValue;
    }

    set toolsCellAlignment(value: LeftOrRightAlignment) {
        this._toolsCellAlignment = value;
        this.syncToolsHandler();
    }

    get toolsHandler(): ToolsHandler {
        return this._toolsHandler;
    }

    private set toolsHandler(value: ToolsHandler) {
        this._toolsHandler = value;
    }

    private syncToolsHandler() {
        if (!this.displayToolsCell) {
            this.toolsHandler?.removeColumn();
            this.toolsHandler = null;
        }
        else {
            if (this.toolsHandler == null) {
                this.toolsHandler = new ToolsHandler(this);
            }
            this.toolsHandler.syncColumnPlacement();
        }
    }

    get largestToolsCellWidth(): number {
        return this._largestToolsCellWidth;
    }

    /**
     * TableRow notifies the Table of the width of its tools cell so that Table can adjust the width of the tools cell
     * in each table row.
     */
    toolsCellAdded(width: number): void {
        if (width > this._largestToolsCellWidth) {
            this._largestToolsCellWidth = width;
            const setFixedWidth = (row: TableRow) => {
                const toolsCell = row.cells.find((cell: TableCell) => {
                    return cell instanceof ToolsTableCell || cell instanceof ToolsHeadingTableCell;
                });
                if (toolsCell != null) {
                    toolsCell.fixedWidth = width;
                }
            };
            this.forEveryRow(true, setFixedWidth);
            this.resetHeadingRowPaddingForScrollbar();
        }
        this.syncColumnResizers();
    }

    private resetHeadingRowPaddingForScrollbar() {
        let scrollbarWidth: number|null = this.scrollbarWidth;
        if (scrollbarWidth === 0) {
            scrollbarWidth = null;
        }
        if (this.headingRow != null) {
            this.headingRow.paddingRight = scrollbarWidth;
        }
    }

    async recalculateVirtualPlaceholders() {
        if (this.virtualized === false)
            return;

        const anyRow = this.allRows?.find(row => row.populatedDOM === true);
        if (this.fixedRowHeightWidth && anyRow) {
            const width = DOMUtil.getElementWidth(anyRow._element) - this.scrollbarWidth;
            this.fixedRowHeightWidth = { ...this.fixedRowHeightWidth, width: width };
            this.allRows?.forEach(row => {
                if (row.populatedDOM !== true)
                    row.placeholderHeightWidth = this.fixedRowHeightWidth;
            });
        }
    }

    private get scrollbarWidth(): number {
        return DOMUtil.isScrollbarVisible(this._tbody) ? DOMUtil.getScrollbarWidth() : 0;
    }

    isFiltered(): boolean {
        return !StringUtil.isEmptyString(this._filterValue);
    }

    isSorted(): boolean {
        return ArrayUtil.isEmptyArray(this._sortInfo) !== true;
    }

    public applySort(fieldName: string, direction: SortDirection = "asc", addingToSort: boolean = false) {
        if (addingToSort === true) {
            if (this.fieldInCurrentSort(fieldName) === true) {
                const message = "Not adding field %s to sort for table %s; the field is already included in the " +
                    "existing sort";
                log.debug(this, message, fieldName, this.id);
                return;
            }
        }
        const selector = this.findSortSelectorsForField(fieldName)?.selector;
        if (selector == null) {
            log.debug(this, "Not adding field %s to sort for table %s; the field has no sort selector", fieldName, this.id);
            return;
        }
        this.sortByField(addingToSort, selector, null, direction);
    }

    private fieldInCurrentSort(fieldName: string) {
        if (this._sortInfo != null) {
            for (const fieldSortInfo of this._sortInfo) {
                if (fieldSortInfo.fieldName === fieldName) {
                    return true;
                }
            }
        }
        return false;
    }

    private addSort(newSort: FieldSortInfo) {
        if (this._sortInfo == null)
            this._sortInfo = [];
        this._sortInfo.push(newSort);
        this.updateSortOrders();
    }

    private removeSort(sortToRemove: FieldSortInfo) {
        sortToRemove.selector.update(null, null, false);
        if (this._sortInfo == null)
            return null;
        ArrayUtil.removeFromArray(this._sortInfo, sortToRemove);
        if (this._sortInfo.length == 0)
            this._sortInfo = null;
        this.updateSortOrders();
    }

    public clearSort() {
        if (this._sortInfo == null)
            return;
        for (let x = this._sortInfo.length - 1; x >= 0; x--) {
            const fieldSortInfo = this._sortInfo[x];
            this.removeSort(fieldSortInfo);
        }
    }

    private getFieldSortInfo(selector: SortSelector): FieldSortInfo {
        if (this._sortInfo == null)
            return null;
        for (const fieldSortInfo of this._sortInfo) {
            if (fieldSortInfo.selector === selector)
                return fieldSortInfo;
        }
        return null;
    }

    get sortInfo(): FieldSortInfo[] {
        return this._sortInfo;
    }

    private updateSortOrders() {
        if (this._sortInfo == null)
            return;
        const displayOrder = this._sortInfo.length > 1;
        let count = 0;
        for (const fieldSortInfo of this._sortInfo) {
            fieldSortInfo.order = ++count;
            fieldSortInfo.displayOrder = displayOrder;
            fieldSortInfo.selector.update(fieldSortInfo.sort, fieldSortInfo.order, fieldSortInfo.displayOrder);
        }
    }

    private advanceSortToNextState(fieldSortInfo: FieldSortInfo, sortDescendingByDefault: boolean) {
        if (fieldSortInfo.sort === null)
            fieldSortInfo.sort = sortDescendingByDefault ? "desc" : "asc";
        else if (fieldSortInfo.sort === "asc")
            fieldSortInfo.sort = sortDescendingByDefault ? null : "desc";
        else if (fieldSortInfo.sort === "desc")
            fieldSortInfo.sort = sortDescendingByDefault ? "asc" : null;
    }

    private updateSortDisplay() {
        if (this.isSorted() !== true)
            return;
        for (const fieldSortInfo of this._sortInfo) {
            fieldSortInfo.selector.update(fieldSortInfo.sort, fieldSortInfo.order, fieldSortInfo.displayOrder);
        }
    }

    private sortFromClick(event: ClickEvent, parentSelector?: SortSelector) {
        const addingToSort = event.domEvent?.ctrlKey === true ||
            event.domEvent?.altKey === true ||
            event.domEvent?.metaKey === true;
        const sortLabel = event.target as SortSelector;
        this.sortByField(addingToSort, sortLabel, parentSelector);
    }

    private sortByField(addingToSort: boolean, selector: SortSelector, parentSelector?: SortSelector, overrideSortDirection?: SortDirection) {
        this.clearDisplayedRows();

        //if clicking on a selector that isn't currently in the sort, we want to add it to the sort
        // -> if the user used ctrl/alt key, add to the existing sort
        // -> else, clear existing sort selectors before adding the new one (so that it's the only one left)
        //if clicking on a selector that is currently in the sort, we want to update or remove it
        // -> advance the selector to the next sort state
        // -> if the result is that the selector is 'unsorted', remove that field from the sort sequence and update remaining sort fields
        //finally, actually sort the table's filtered rows using the sort info that results from the above

        const sortFieldInfo = selector.sortFieldInfo;
        let fieldSortInfo = this.getFieldSortInfo(selector);
        if (fieldSortInfo == null) {
            if (addingToSort !== true)
                this.clearSort();
            fieldSortInfo = {
                fieldName: parentSelector?.fieldName != null ? parentSelector.fieldName : selector.fieldName,
                selector: selector,
                sort: null,
                order: 1,
                sortNullsAtEnd: sortFieldInfo.sortNullsAtEnd,
                displayOrder: this._sortInfo?.length > 0
            };
            if (overrideSortDirection == null)
                this.advanceSortToNextState(fieldSortInfo, sortFieldInfo.sortDescendingByDefault);
            else
                fieldSortInfo.sort = overrideSortDirection;
            this.addSort(fieldSortInfo);
        }
        else {
            fieldSortInfo.sortNullsAtEnd = sortFieldInfo.sortNullsAtEnd;
            if (overrideSortDirection == null)
                this.advanceSortToNextState(fieldSortInfo, sortFieldInfo.sortDescendingByDefault);
            else
                fieldSortInfo.sort = overrideSortDirection;
            if (fieldSortInfo.sort == null) {
                this.removeSort(fieldSortInfo);
            }
        }
        this.updateSortDisplay();
        for (const row of this._sortRows([...this.filteredRows]))
            this.displayRow(row);
        this.resetIndexAndSequence(this.rows, true, false);
        this._syncRowsDraggable();
        this.applyLastScrollLeft();
    }

    private _compareRows(a: TableRow, b: TableRow): number {
        log.debug(this, "comparing  a: %o  b: %o  sortInfo: %o", a, b, this._sortInfo);
        if ((this._sortInfo?.length ?? 0) == 0) {
            return 0;
        }
        for (const fieldSortInfo of this._sortInfo) {
            const valueA = SortUtil.getSortTestValue(a.data, fieldSortInfo.fieldName);
            const valueB = SortUtil.getSortTestValue(b.data, fieldSortInfo.fieldName);
            const compareResult = SortUtil.compareTwoValues(valueA, valueB, fieldSortInfo.sort,
                fieldSortInfo.sortNullsAtEnd);
            if (compareResult !== 0)
                return compareResult;
        }
        return SortUtil.compareTwoValues(a.index, b.index, "asc", false); // use the existing indexes as a tie-breaker
    }

    clearColumns() {
        // Clear the headingRow; any generated columns will be re-added within `set columns()`
        this.headingRow.clear();
        this.columns = [];
        for (const row of this._allRows) {
            if (row.populatedDOM) {
                row.clear();
                row.addVirtualizedPlaceholder();
                this._observer.observe(row._element);
            }
        }
    }

    clear() {
        this.clearRows();
        this.clearColumns();
    }

    public override isEmpty(): boolean {
        return ArrayUtil.isEmptyArray(this._allRows);
    }

    getColumnIndex(identifier: number | TableColumn | string): number {
        if (typeof identifier === "number")
            return identifier;
        if (identifier instanceof TableColumn)
            return this.columns.indexOf(identifier);
        // If a string is provided, attempt to match it against the cell ID (from the layout json definition), or the
        // ID of the headingCell, or the headingCell's caption.
        for (let x = 0; x < this.columns.length; x++) {
            const column = this.columns[x];
            if (column?.cellDef?.def?.id === identifier ||
                column?.headingCell?.id === identifier ||
                column?.headingCell?.caption === identifier)
                return x;
        }
        return -1;
    }

    /**
     * Retrieves a column based on a variable input.
     *
     * @param columnName The column's name will be used to find the column.
     */
    getColumnByCaption(columnName: string): TableColumn {
        const index = this.getColumnIndex(columnName);
        if(index < 0) {
            log.debug("Unable to find column index for " + columnName);
            return;
        }
        return this._columns[index];
    }

    /**
     * Set a column's visibility based on variable inputs.
     *
     * @param col The column that's visibility will be set.
     * @param isVisible Whether or not the column is visible.
     */
    setColumnVisibility(col: TableColumn, isVisible: boolean) {
        if(!col) {
            log.debug("Unable to set column visibility to NULL column.");
            return;
        }
        col.cellDef.cellProps.visible = isVisible;
        col.headingCell.visible = isVisible;
    }

    /**
     * Allows for the removal of a normal table column based on a variable input.  The removal is disallowed if the
     * column being removed is a generated/special column.
     *
     * @param identifier The column's index number, or the TableColumn object, or a string.  If a string is provided,
     * it will be used to find a column where the string value is either A) the cell ID (from the layout json
     * definition) or B) the ID of the column's headingCell or C) the caption of the column's headingCell.
     */
    removeColumn(identifier: number | TableColumn | string): TableColumn {
        return this.internalRemoveColumn(identifier, false);
    }

    /**
     * This method exists only so we can remove a generated/special column.
     *
     * This method should not be called from outside the library.
     *
     * @param identifier The column's index number, or the TableColumn object, or a string.  If a string is provided,
     * it will be used to find a column where the string value is either A) the cell ID (from the layout json
     * definition) or B) the ID of the column's headingCell or C) the caption of the column's headingCell.
     */
    _removeGeneratedColumn(identifier: number | TableColumn | string): TableColumn {
        return this.internalRemoveColumn(identifier, true);
    }

    private internalRemoveColumn(identifier: number | TableColumn | string, skipColumnCheck: boolean): TableColumn {
        const index = this.getColumnIndex(identifier);
        if (index < 0) {
            log.debug(this, "Cannot remove column, column could not be located: %o", identifier);
            return null;
        }

        const column = this.columns[index];
        if (!column.canBeRemoved() && !skipColumnCheck) {
            log.debug(this, "Cannot remove column: %o", column);
            return null;
        }

        this.columns.splice(index, 1);
        const callback = (row: TableRow) => row._removeColumn(index);
        this.forEveryRow(true, callback);
        this.resetColumnIndexes();
        log.debug(this, "Removed column: %o", column);
        return column;
    }

    private resetColumnIndexes() {
        let count = -1;
        for (const column of this.columns) {
            column.index = ++count;
        }
    }

    private indexOf(row: TableRow) {
        return this.rows.indexOf(row);
    }

    selectRow(row: TableRow, selectionType: SelectionType, event: DomEvent) {
        if (this.selectionMode == null || this.selectionMode === SelectionMode.NONE)
            return;

        if (window.getSelection().type === "Caret") {
            this.clearBrowserRangeSelection(event?.target);
        }

        if (row == null) {
            this.clearSelectedRows();
            return;
        }

        if (this.selectionMode === SelectionMode.SINGLE)
            selectionType = SelectionType.SINGLE;
        if (selectionType === SelectionType.SINGLE)
            this.handleSingleSelection(row, event);
        else if (selectionType === SelectionType.DISTINCT)
            this.handleDistinctSelection(row, event);
        else if (selectionType === SelectionType.RANGE)
            this.handleRangeSelection(row, event);

        if (row === this.quickAddRow) {
            row.selected = true;
        } else if (this.quickAddRow != null) {
            this.quickAddRow.selected = false;
        }
    }

    /**
     * Clears text selection while preserving cursor position in input fields.
     * This prevents shift-clicking rows from selecting HTML elements between click points.
     */
    private clearBrowserRangeSelection(target: EventTarget) {
        if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) {
            window.getSelection().removeAllRanges();
            return;
        }

        const originalCursorPos = target.selectionStart || 0;

        window.getSelection().removeAllRanges();

        // Restore cursor to original position after clearing text selection
        target.setSelectionRange(originalCursorPos, originalCursorPos);
    }

    private handleSingleSelection(row: TableRow, event: DomEvent | ClickEvent) {
        const sel = this.selectedRows;
        if (sel?.length === 1 && sel[0] === row) {
            if (!(event instanceof ClickEvent) || event.hasModifiers({ ctrlKey: true })) {
                this.clearSelectedRows(event as DomEvent);
            }
        }
        else {
            this._setSelectedRowsInternal([row], event as DomEvent);
        }
    }

    private handleDistinctSelection(row: TableRow, event: DomEvent) {
        const newSel = this.selectedRows;
        const index = newSel.indexOf(row);
        if (index < 0) {
            newSel.push(row);
        }
        else
            newSel.splice(index, 1);
        this._setSelectedRowsInternal([...newSel], event);
    }

    private handleRangeSelection(row: TableRow, event: DomEvent) {
        const sel = this.selectedRows;
        let newSel: TableRow[];
        if (sel.length === 0)
            newSel = [row];
        else {
            const thisSelIndex = this.rows.indexOf(row);
            const firstSelIndex = this.rows.indexOf(sel[0]);
            const lastSelIndex = this.rows.indexOf(sel[sel.length - 1]);
            const minSelIndex = Math.min(thisSelIndex, firstSelIndex);
            const maxSelIndex = Math.max(thisSelIndex, lastSelIndex);
            newSel = [];
            for (let index = minSelIndex; index <= maxSelIndex; index++)
                newSel.push(this.rows[index]);
        }
        this._setSelectedRowsInternal(newSel, event);
    }

    public confirmRowDelete(tableRowOrArray: TableRow | TableRow[]):  Promise<boolean> {
        let promptSuffix:string;
        if (tableRowOrArray instanceof TableRow) {
            promptSuffix = "this record";
        } else {
            promptSuffix = `the selected ${tableRowOrArray.length === 1 ? "record" : "records"}`;
        }
        return ReflectiveDialogs.showYesNo(`Are you sure you want to delete ${promptSuffix}?`, "Confirm Delete");
    }

    /**
     * Removes a row from the table.  Note that this method DOES NOT delete the record from the DataSource/database
     * (when the table is bound in such a fashion).
     *
     * @param row The parameter can specify either of the following:
     * - The index of the row to be removed.  Note that this is the index of the row within the displayed data (which
     * may be filtered and/or sorted, and thus not include all of the rows that the table contains).  It IS NOT possible
     * to provide the parameter as the index number to remove a row that is not currently displayed.
     * - The ModelRow data that the row to be removed contains/matches.  The matching TableRow will be found (one that
     * either contains the ModelRow as its data, or one that contains a ModelRow with the same key data), and that
     * TaleRow will be removed.  It IS possible to provide the parameter as the  ModelRow data to remove a row that is
     * not currently displayed.
     */
    removeRow(row: number | ModelRow) {
        const rows = this.filteredRows;
        const rowIndex = typeof row === "number" ? row : this.getIndexFromData(row, rows);
        // capture the visual position in the displayed rows before it's removed. This may differ from the rowIndex if the table is sorted.
        const visualIndex = this.indexOf(rowIndex >= 0 ? rows[rowIndex] : null);
        let rowBeingRemoved = this.internalRemoveRow(rowIndex, this.filteredRows);

        // If the table is filtered, we also need to remove the row from the allRows array
        if (rows !== this._allRows) {
            let allRowsIndex = -1;
            if (rowBeingRemoved != null) {
                // If the row being removed was also being displayed, find its allRows index using the row object.
                // This is just a little better than the subsequent else if, which does a deeper comparison.
                allRowsIndex = this._allRows.indexOf(rowBeingRemoved);
            } else if (typeof row !== "number") {
                allRowsIndex = this.getIndexFromData(row, this.allRows);
            }

            rowBeingRemoved = this.internalRemoveRow(allRowsIndex, this._allRows);
        }

        // Only fire the event and notify the DataSource if the row was removed from _allRows
        if (rowBeingRemoved != null) {
            if (visualIndex >= 0)
                this.resetEvenOddRowColors(visualIndex);
            this.headingPanel.checkToolAvailability();
            this.fireContentsChangedListener(new TableContentsChangedEvent(this, rowBeingRemoved, TableAction.DELETE));
            this.dataSource?.notifyHasChangedComponents();
        }
    }

    /**
     * Within the provided TableRow array, find the first row whose data matches the provided ModelRow.  The match could
     * be based on the same ModelRow object being found in one of the TableRows, or one of the TableRows containing a
     * ModelRow with the same key data.
     *
     * @param tableRowData The ModelRow to use when searching.
     * @param rows The array of TableRows to search.
     * @returns The index of the matching row in the provided array.
     */
    getIndexFromData(tableRowData: ModelRow, rows: TableRow[]) {
        for (let i = 0; i < rows.length; i++) {
            const rowInArray = rows[i];
            if (tableRowData === rowInArray?.data) {
                log.debug("Found same row data in TableRow array: %o", rowInArray);
                return i;
            }
            if (rowInArray.data instanceof ModelRow &&
                (this.dataSource?.compareRows(tableRowData, rowInArray.data) ?? false)) {
                return i;
            }
        }
        return -1;
    }

    private internalRemoveRow(rowIndex: number, rows: TableRow[]): TableRow {
        if (rowIndex >= 0) {
            const rowBeingRemoved = rows[rowIndex];
            rows.splice(rowIndex, 1);
            this.hideDisplayedRow(rowBeingRemoved);
            this.handleEmptyComponent();
            this.resetIndexAndSequence(rows);
            return rowBeingRemoved;
        }
        else {
            log.debug("Not removing row from array; provided index < 0");
            return null;
        }
    }

    addRow(data, rowProps?: Partial<TableRowProps>, options?: Partial<TableAddRowOptions>): TableAddRowResult {
        const row = TableRow.create(this);
        row.setDesigner(this.getDesigner());
        row.owner = this.owner;
        const props: Partial<TableRowProps> = {
            ...this._rowProps,
            data: data,
            expanded: data.expanded,
            virtualized: this.virtualized,
            placeholderHeightWidth: this.fixedRowHeightWidth,
            ...rowProps
        };
        if (this.rowModeControlType === RowModeControlType.ALWAYS_EDIT) {
            if (props.mode == null || props.mode === TableRowMode.NONE)
                props.mode = TableRowMode.UPDATE;
        }
        row.setProps(props);
        this._allRows.push(row);
        row.index = this._allRows.length - 1;
        this.applyRowProps(row);

        if (options?.addToData === true) {
            if (this._sequenceField != null && data instanceof ModelRow)
                data.set(this._sequenceField, this.data.length + 1, this);
            if (this.dataSource != null)
                this.dataSource.addRow(data, null, false);
            else
                this.data.push(data);
            this.dataSource?.notifyHasChangedComponents(true);
            this.fireContentsChangedListener(new TableContentsChangedEvent(this, row, TableAction.ADD));
        }
        if (options?.display === true) {
            if (this.rowPassesFilter(row) === true) {
                const insertPos = this._getRowInsertPos(row);
                this.displayRow(row, insertPos);
                this._filteredRows?.splice(insertPos, 0, row);
                this.refreshRowCountPanel();
            }
        }
        this._syncRowsDraggable();
        let saveSuccessful: boolean;
        const shouldSave = options?.addToData === true && (options?.save === true || (options?.save == null && this.persistChangesImmediately === true));
        if (shouldSave === true)
            saveSuccessful = row.saveChanges();
        return { row: row, saveSuccessful: saveSuccessful };
    }

    handleDataDisplay(event: FieldUpdateEvent, data: ModelRow, allData: ModelRow[], rowIndex: number) {
        const tableRow = this.findTableRow(event.row);
        tableRow?.displayDataForField(event, event.row, allData, rowIndex);
    }

    public findTableRow(row: ModelRow): TableRow {
        for (const tableRow of this.rows) {
            if (tableRow.data === row)
                return tableRow;
        }
        return null;
    }

    private _getRowInsertPos(rowToInsert: TableRow, rows: TableRow[] = this.rows): number {
        if (rows == null || !this.isSorted()) {
            return rows.length;
        }
        else {
            const result = BinarySearch.search(rows, rowToInsert, (a, b) => this._compareRows(a, b));
            return result.foundIndex >= 0 ? result.foundIndex : result.insertIndex;
        }
    }

    private _sortRows(rows: TableRow[]): TableRow[] {
        if (rows != null && this.isSorted()) {
            rows.sort((a, b) => this._compareRows(a, b));
        }
        return rows;
    }

    displayRow(row: TableRow, insertPos: number = -1, updateEvenOddRowColors: boolean = true) {
        this.hasDisplayedData = true;
        let insertedIndex = insertPos;
        if (insertPos === -1) {
            this.rows.push(row);
            this.insertIntoTbody(row._element);
            insertedIndex = this.rows.length - 1;
        }
        else {
            const existingRowAtInsertPos = this.rows[insertPos];
            this.rows.splice(insertPos, 0, row);
            // If the row that is above where the new row will be inserted is expanded, make sure we insert the new row
            // below the expansion
            this.insertIntoTbody(row._element, existingRowAtInsertPos?._element);
        }

        // Reset the even/odd row colors for the inserted row and all of the rows that follow it
        if (updateEvenOddRowColors === true) {
            this.resetEvenOddRowColors(insertedIndex);
        }

        if (this.virtualized)
            this._observer.observe(row._element);
        else
            row.populateDOMIfNeeded();
        this.debouncedCheckHeadingPanelToolAvailability();
        this.handleEmptyComponent();
        return row;
    }

    private setPlaceholderRowDimensions = (event: TableRowDisplayEvent) => {
        const row = event.getTableRow();
        const rowDOMRect = row.bounds;
        const height = rowDOMRect?.height;
        const width = rowDOMRect?.width;
        let needToUpdate = false;
        if (this.fixedRowHeightWidth == null && height > 0 && width > 0) {
            this.fixedRowHeightWidth = { height, width };
            needToUpdate = true;
        }
        else if (this.fixedRowHeightWidth != null && width > this.fixedRowHeightWidth.width)
        {
            this.fixedRowHeightWidth.width = width;
            needToUpdate = true;
        }
        if (needToUpdate) {
            this.removePlaceholderRowDisplayListener = true;
            this.allRows?.forEach(row => {
                row.placeholderHeightWidth = this.fixedRowHeightWidth;
            });
        }
    }

    public get evenRowColor(): Color {
        if (this._designer != null)
            return undefined;
        return this._evenRowColor ?? UserSettings.get().table_even_row_color;
    }

    public set evenRowColor(value: Color) {
        this._evenRowColor = value;
    }

    public get oddRowColor(): Color {
        if (this._designer != null)
            return undefined;
        return this._oddRowColor ?? UserSettings.get().table_odd_row_color;
    }

    public set oddRowColor(value: Color) {
        this._oddRowColor = value;
    }

    public get ignoreRowColorSetting(): boolean {
        return this._ignoreRowColorSetting;
    }

    public set ignoreRowColorSetting(value: boolean) {
        this._ignoreRowColorSetting = value;
    }

    private resetEvenOddRowColors(startingIndex: number = 0) {
        if (this._designer != null) {
            return;
        }
        if (this.ignoreRowColorSetting === true) {
            log.debug("Not updating even/odd row colors, the ignoreRowColorSetting property has been set");
            return;
        }
        for (let x=startingIndex ; x<this.rows.length ; x++) {
            this.updateRowEvenOddColor(x, this.rows[x]);
        }
    }

    private updateRowEvenOddColor(index: number, row: TableRow) {
        // Index for determining if row is 'even' or 'odd' is not the row's index, but where it is visually on the page
        // Example: the first row in the table (index = 0) is treated as an odd row, since it's the first visible row
        // Accomplish this by adding 1 to the index
        const colorToUse = ((++index % 2) === 0) ? this.evenRowColor : this.oddRowColor;
        row.evenOddColor = colorToUse;
    }

    async setRowsExpanded(expand: boolean, rowFilter?: (row: TableRow) => boolean, doAfterRowExpansion?: () => void) {
        const expandPromise = (this.expandRowsPromise || Promise.resolve()).then(async () => {
            const tableRows = this.rows?.filter(row => row.expanded !== expand && (!rowFilter || rowFilter(row)));
            if (!tableRows || tableRows.length === 0)
                return;
            try {
                for (const row of tableRows) {
                    await row.setExpandedAsync(expand);
                }
                if (doAfterRowExpansion != null) {
                    doAfterRowExpansion();
                } else if (expand) {
                    ArrayUtil.getLastElement(tableRows)?.scrollIntoView();
                }
                return true;
            } finally {
                this.expandRowsPromise = null;
            }
        });
        this.expandRowsPromise = expandPromise;
        return expandPromise;
    }

    async _createExpansionComponent(row: TableRow): Promise<Component> {
        let component: Component = null;
        if (this.expandComponentSource instanceof Component) {
            component = CloneComponent.clone({
                component: this.expandComponentSource,
                id: this.getExpandComponentId(this.expandComponentSource.id, row.index),
                appendComponentId: this._designer == null
            });
            await component.ensureLoaded();
            if (component instanceof Container) {
                component.setPropOnChildren("boundRow", row.data);
            }
        } else if (typeof this.expandComponentSource === "function") {
            component = this.expandComponentSource(row);
            await component.ensureLoaded();
        } else {
            component = await new ComponentDeserializer({
                owner: null,
                def: this.expandComponentSource.def,
                designer: this._designer,
                defaultPropValues: {boundRow: row.data}
            }).deserializeSingleComponent();
        }
        this.fireRowExpandListeners(row, component);
        this.displayRowExpandComponent(row, component);
        return component;
    }

    private getExpandComponentId(currentId?: string, rowIndex?: number): string {
        let result = currentId;
        if (StringUtil.isEmptyString(currentId) === true)
            result = this.id + "-expand";
        if (rowIndex != null)
            result += "-" + rowIndex;
        return result;
    }

    private displayRowExpandComponent(row: TableRow, component: Component) {
        this.createExpansionElement(row, component);
        this.insertExpansionElement(row);
        if (this.data != null) {
            const rowData = row.data;
            component.displayData(rowData, this.data, row.index);
        }
        if (this.expandRowsPromise == null) {
            row.scrollIntoView();
        }
    }

    protected createExpansionElement(row: TableRow, expansionComponent: Component) {
        const expansionElement = document.createElement("tr");
        expansionElement.style.display = "block";
        expansionElement.style.alignItems = "flex-start";
        const expansionCell = document.createElement("td");
        expansionCell.style.padding = "0px";
        expansionElement.style.borderBottom = "3px solid " + getThemeColor("primary");
        expansionElement.appendChild(expansionCell);
        expansionCell.appendChild(expansionComponent._element);
        expansionCell.style.display = "block";
        row._setExpansionElement(expansionElement);
    }

    protected insertExpansionElement(row: TableRow) {
        row.setBorderBottom(true);
        this.insertIntoTbody(row.expansionElement, row._element.nextSibling)
    }

    _removeExpansionElement(row: TableRow, fireEvent: boolean = true) {
        const expansionElement = row.expansionElement;
        if (expansionElement == null || this._tbody.contains(expansionElement) === false) {
            return;
        }
        row.setBorderBottom(false);
        this._tbody.removeChild(expansionElement);
        row._setExpansionElement(null);
        row._setExpansionComponent(null);
        if (fireEvent) {
            this.fireListeners(_rowCollapseListenerDef, new TableRowExpansionEvent(row, this.expandComponentSource, this, row.expanded));
        }
    }

    _serializeProp(key: string, value: any): any {
        //control serialization of even/odd row colors here..this allows normal getters to not render
        //row colors in the designer (so a user's default row colors won't be serialized into the layout)
        if ("evenRowColor" === key)
            return this._evenRowColor;
        if ("oddRowColor" === key)
            return this._oddRowColor;
        return value;
    }

    _serializeNonProps() {
        let result = "";
        result += this.serializeColumns();
        result += this.serializeRowDef();
        result += this.serializeExpandComponent();
        result += this.serializeTools();
        result = StringUtil.removeTrailingString(result, ",\n");
        result += "\n}\n";
        return result;
    }

    private serializeColumns(): string {
        let result = "";
        if (ArrayUtil.isEmptyArray(this.headingRow?.cells) === false) {
            result += "\"columns\": [\n";
            let columnsJson = "";
            for (let i = 0; i < this.headingRow.cells.length; i++) {
                const headingCell = this.headingRow.cells[i];
                const column = this.columns.find(column => column.headingCell === headingCell);
                if (column.isSerialized()) {
                    if (!StringUtil.isEmptyString(columnsJson)) {
                        columnsJson += ",\n";
                    }
                    columnsJson += "{\n";
                    columnsJson += "\"heading\":\n" + serializeComponents(headingCell, null);
                    columnsJson += ",\n";
                    columnsJson += "\"cell\": " + serializeComponents(this.designerRow.cells[i], null) + "\n";
                    columnsJson += "}";
                }
            }
            result += columnsJson;
            result += "],\n";
        }
        return result;
    }

    private serializeRowDef(): string {
        let result = "";
        const designerRow = this.designerRow;
        if (designerRow != null) {
            const serialized = serializeProps(designerRow);
            if (serialized.length > 0) {
                result += "\"rowProps\": {\n";
                result += serialized;
                result = StringUtil.removeTrailingString(result, ",\n");
                result += "\n},\n";
            }
        }
        return result;
    }

    private serializeExpandComponent(): string {
        let result = "";
        if (this.expandComponentSource instanceof Container && !this.expandComponentSource.isEmpty()) {
            let def = serializeComponents(this.expandComponentSource, null);
            const parsed = JSON.parse(def);
            if (parsed.backgroundColor === defaultExpandBackground)
                delete parsed.backgroundColor;
            if (parsed.borderColor === defaultExpandColor) {
                delete parsed.borderWidth;
                delete parsed.borderColor;
            }
            def = JSON.stringify(parsed);
            result += "\"expandComponent\": " + def + ",\n";
        }
        return result;
    }

    private serializeTools(): string {
        let result = "";
        result += this.headingPanel.serializeCustomTools(this.serializeToolsPanel);
        result += this.serializeToolsPanel(this._additionalRowToolsPanel.components, "additionalRowTools");
        return StringUtil.removeTrailingString(result, ",\n");
    }

    private serializeToolsPanel(components: Component[], name: string): string {
        let result = "";
        if (components?.length > 0)
            result = "\"" + name + "\": " + serializeComponents(components, null) + ",\n";
        return result;
    }

    public get rowEditResolveMode(): TableRowEditResolveMode {
        return this._rowEditResolveMode == null ? TableRowEditResolveMode.DIRECT_FROM_EDITOR : this._rowEditResolveMode;
    }

    public set rowEditResolveMode(value: TableRowEditResolveMode) {
        this._rowEditResolveMode = value;
    }

    override async _deserializeSpecialProps(props: DeserializeProps): Promise<string[]> {
        const compSpecial = await super._deserializeSpecialProps(props);
        const compDef = props.def;
        if (compDef.rowProps != null)
            this.rowProps = { ...compDef.rowProps };
        await this.deserializeTools((component: Component) => this.addLeftTool(component),
            {...props, def: compDef.leftTools});
        await this.deserializeTools((component: Component) => this.addCenterTool(component),
            {...props, def: compDef.centerTools});
        await this.deserializeTools((component: Component) => this.addRightTool(component),
            {...props, def: compDef.rightTools});

        this.additionalRowToolsDef = compDef.additionalRowTools;

        const columnDefs = this.getSerializedColumnDefinitions(compDef);
        if (columnDefs != null) {
            for (let i = 0; i < columnDefs.length; i++) {
                const col = new TableColumn();
                if (typeof columnDefs[i].heading !== "string") {
                    const headingProps = { ...props.defaultPropValues, table: this, row: this.headingRow };
                    // We provide the custom componentCreationCallback below only so we can call the TableHeadingCell
                    // constructor when deserializing the heading cell.  The more straightforward way to do this would
                    // have been to change all existing layouts to include the 'headingcell' serialization name, but I
                    // wanted to avoid creating that many conflicts in the early-adopter era.  Maybe someday we make
                    // that change, at which time we would no longer need the componentCreationCallback here.
                    const deserializeProps: DeserializeProps = {
                        ...props,
                        def: columnDefs[i].heading,
                        defaultPropValues: headingProps,
                        componentCreationCallback: (type: string, props: any) => {
                            if ("cell" !== type) {
                                // We shouldn't end up here because HeaderTableCell def.components should not be defined
                                // and HeadingTableCell overrides _deserializeSpecialProps
                                return null;
                            }
                            return ComponentTypes.createComponentOfType("headingcell", props);
                        }
                    };
                    col.headingDef = await new ComponentDeserializer(deserializeProps).deserializeSingleComponent();
                }
                else
                    col.headingDef = columnDefs[i].heading;
                if (typeof columnDefs[i].cell !== "string") {
                    const cellProps = { ...props.defaultPropValues, table: this };
                    col.cellDef = { def: columnDefs[i].cell, owner: props.owner, cellProps: cellProps, dataSources: props.dataSources };
                }
                else
                    col.cell = columnDefs[i].cell;
                this.addColumn(col);
            }
        }
        if (compDef.expandComponent != null)
            this.expandComponentSource = { def: compDef.expandComponent, dataSources: props.dataSources };

        if (this._designer == null) {
            this.headingPanel?.addMountListener(() => {
                const layout = this.getRootLayout();
                if (this == layout?.getFirstFocusableChild())
                    this.focusSearchFilter();
            })
        }

        return [...compSpecial, "columns", "expandComponent", "additionalRowTools"];
    }

    protected getSerializedColumnDefinitions(compDef: any): any {
        return compDef.columns;
    }

    private async deserializeTools(addToolMethod: (component: ComponentCreator) => Component, props: DeserializeProps) {
        const tools = await new ComponentDeserializer({...props}).deserialize();
        tools?.forEach(component => addToolMethod(component));
    }

    getFilterImage(): Image {
        return this.textboxFilter?.imagePre;
    }

    public get filterClearButtonVisible(): ClearButtonVisible {
        return this._filterClearButtonVisible ?? this.getPropertyDefinitions().filterClearButtonVisible.defaultValue;
    }

    public set filterClearButtonVisible(value: ClearButtonVisible) {
        this._filterClearButtonVisible = value;
        if (this.textboxFilter != null)
            this.textboxFilter.clearButtonVisible = value;
    }

    public get filterAlignment(): FilterAlignment {
        return this.searchFilterMode === SearchFilterMode.COLLAPSED ? Alignment.RIGHT :
            this._filterAlignment ?? this.getPropertyDefinitions().filterAlignment.defaultValue;
    }

    public set filterAlignment(value: FilterAlignment) {
        this._filterAlignment = value;
        this.headingPanel.syncSearchFilterFields();
    }

    public get searchFilterMode(): SearchFilterMode {
        return this._searchFilterMode ?? this.getPropertyDefinitions().searchFilterMode.defaultValue;
    }

    public set searchFilterMode(value: SearchFilterMode) {
        if (this.searchFilterMode === value)
            return;
        this._searchFilterMode = value;
        if (value === SearchFilterMode.COLLAPSED)
            this.filterAlignment = Alignment.RIGHT;
        this.headingPanel.syncSearchFilterFields();
    }

    /**
     * Submits a search request to populate the table based on the current search text.  This method should not be
     * called from outside of the component library.
     */
    _searchFromServer() {
        this._filterValue = null;
        if (this.dataSource == null) {
            return;
        }
        if (this.textboxSearch.hasText()) {
            this.dataSource.search({ quick_search: this.textboxSearch.text });
        }
        else if (this.dataSource.data.length > 0) {
            this.dataSource.clear();
            this.dataSource.displayDataInBoundComponents();
            this.refreshRowCountPanel();
        }
    }

    addLeftTool(tool: ComponentCreator): Component {
        return this.headingPanel.addLeftTool(tool);
    }

    addCenterTool(tool: ComponentCreator): Component {
        return this.headingPanel.addCenterTool(tool);
    }

    addRightTool(tool: ComponentCreator): Component {
        return this.headingPanel.addRightTool(tool);
    }

    removeTool(tool: Component) {
        this.headingPanel.removeTool(tool);
    }

    get filterValue(): string {
        return this._filterValue;
    }

    set filterValue(value: string) {
        this._filterValue = value != null ? value.trim() : value;
        this.debouncedApplyFilters();
    }

    private panelSearchFilter: any;

    public setFilter(value: string) {
        this.textboxFilter.text = value;
    }

    resetFilter() {
        this.filterValue = null;
        this.textboxFilter.text = "";
        this.refreshRowCountPanel();
        if (this.isSorted())
            this._allRows.sort((a, b) => this._compareRows(a, b));
        for (const row of this._allRows) {
            row.expanded = false;
            this.displayRow(row);
        }
        this.applyLastScrollLeft();
    }

    /**
     * Clears the filter value string without affecting the table.  This method should not be called from outside of the
     * component library.
     */
    _clearFilterValueSilently() {
        this._filterValue = null;
    }

    get _designer(): DesignerInterface {
        return super._designer;
    }

    set _designer(value: DesignerInterface) {
        super._designer = value;
        this.headingRow._designer = value;
        this.headingPanel._designer = value;
        if (value != null) {
            value.addDesignerContainerProperties(this, 100, 200, null, false);
            this.resetHeadingRowPaddingForScrollbar();
            this.addDesignerRow();
            this.syncDesignerExpandArea(null);
            this.syncDesignerRowAdditionalToolsPanel();
        }
        this.syncDesignerToolsHandler();
    }

    get designerToolsHandler(): DesignerToolsHandler {
        return this._designerToolsHandler;
    }

    private set designerToolsHandler(value: DesignerToolsHandler) {
        this._designerToolsHandler = value;
    }

    private syncDesignerToolsHandler() {
        if (this._designer == null || !this._designer.allowsDrop) {
            this.designerToolsHandler?.removeColumn();
            this.designerToolsHandler = null;
        }
        else {
            if (this.designerToolsHandler == null) {
                this.designerToolsHandler = new DesignerToolsHandler(this);
            }
            this.designerToolsHandler.syncColumnPlacement();
        }
    }

    _createNewColumnForDesigner(): TableColumn {
        const dataSources = {};
        if (this.dataSource != null) {
            dataSources[this.dataSource.id] = this.dataSource;
        }
        return new TableColumn({
            headingDef: {
                caption: "Column " + (this.normalColumns.length + 1),
                _designer: this._designer },
            cellDef: {
                cellProps: { table: this },
                dataSources,
                def: { type: "cell" }
            }
        });
    }

    focus() {
    }

    focusSearch() {
        this.textboxSearch.focus();
    }

    focusSearchFilter() {
        this.headingPanel.focusSearchFilter();
    }

    getSpecialDesignerDropTarget(dropTarget: DropTargetPanel) {
        if (this._panelDesignerExpansionDropTarget != null && dropTarget._element === this._panelDesignerExpansionDropTarget._element && this.expandComponentSource == null) {
            this._designer.modified();
            const exp = new Panel({ _designer: this._designer, id: this.getExpandComponentId(), backgroundColor: defaultExpandBackground, borderWidth: 1, borderColor: defaultExpandColor, fillRow: true });
            this._element.replaceChild(exp._element, this._panelDesignerExpansionDropTarget._element);
            this.expandComponentSource = exp;
            return this.expandComponentSource;
        }
    }

    _handleDesignerSwitchAdjacent(component: Component, by: number): boolean {
        if (this._designer == null) {
            throw new Error("This command can only be used in the UI Designer or McLeod Tailor");
        }
        if (component instanceof TableCell) {
            for (let i = 0; i < this.columns.length; i++) {
                if (component === this.columns[i].headingCell) {
                    const switchIndex = i + by;
                    if (switchIndex >= 0 && switchIndex <= this.columns.length) {
                        return this.switchColumns(i, switchIndex);
                    }
                }
            }
        }
        return false;
    }

    /**
     * This method allows for an ad-hoc table row to be added to the top of the table body (before the quick add row, if
     * it is rendered at the top of the table).  It is not intended for use outside of the library.
     * @param row The TableRow that will be added to the top of the table body
     */
    _addAdhocTopRow(row: TableRow) {
        if (this.adhocTopRows == null) {
            this.adhocTopRows = [];
        }
        this.markAsAdhocTopRow(row);
        ArrayUtil.addNoDuplicates(this.adhocTopRows, row);
        this.layoutAdhocTopRows();
    }

    private get adhocTopRowCount(): number {
        return ArrayUtil.getLength(this.adhocTopRows);
    }

    private markAsAdhocTopRow(row: TableRow|Element) {
        const element = row instanceof TableRow ? row._element : row;
        element["__adhocTopRow"] = true;
    }

    private isAdhocTopRow(row: TableRow|Element): boolean {
        const element = row instanceof TableRow ? row._element : row;
        return element?.["__adhocTopRow"] === true;
    }

    /**
     * This method inserts a TableRow at the top of the table body (above any 'normal' table rows, and the quick add row
     * if it is present).
     * @param row The row to be inserted.
     */
    private layoutAdhocTopRows() {
        if (this.adhocTopRowCount === 0) {
            return;
        }

        // Remove all adhoc top rows
        this.adhocTopRows.forEach(row => {
            if (this._tbody.contains(row._element)) {
                this._tbody.removeChild(row._element);
            }
        });

        // Insert the adhoc top rows before the quick add row, or, if the quick add row is not present, before the first
        // normal table row.
        let insertBeforeElement: Node;
        if (this.quickAddRow != null && this._tbody.contains(this.quickAddRow._element)) {
            insertBeforeElement = this.quickAddRow._element;
        } else {
            insertBeforeElement = this._tbody.firstChild;
        }
        this.adhocTopRows.forEach(row => this.insertIntoTbody(row._element, insertBeforeElement));
    }

    /**
     * Moves a normal column.  The move is disallowed if either the column being moved, or the column is replaces, is a
     * generated/special column.
     */
    moveColumn(oldIndex: number, newIndex: number) {
        this.internalMoveColumn(oldIndex, newIndex, false);
    }

    /**
     * This method exists only so we can move a generated/special column after it has been added to the table.
     *
     * This method should not be called from outside the library.
     */
    _moveGeneratedColumn(oldIndex: number, newIndex: number) {
        this.internalMoveColumn(oldIndex, newIndex, true);
    }

    private internalMoveColumn(oldIndex: number, newIndex: number, skipColumnCheck: boolean) {
        if (!this.validateColumnSwitch(oldIndex, newIndex, skipColumnCheck)) {
            return false;
        }
        ArrayUtil.moveArrayElement(this.columns, oldIndex, newIndex);
        const callback = (row: TableRow) => row._moveColumn(oldIndex, newIndex);
        this.forEveryRow(true, callback);
        this.resetColumnIndexes();
    }

    switchColumns(index1: number, index2: number): boolean {
        if (!this.validateColumnSwitch(index1, index2, false)) {
            return false;
        }
        ArrayUtil.switchArrayElements(this.columns, index1, index2);
        const callback = (row: TableRow) => row._switchColumns(index1, index2);
        this.forEveryRow(true, callback);
        this.resetColumnIndexes();
        return true;
    }

    private validateColumnSwitch(index1: number, index2: number, skipColumnCheck: boolean): boolean {
        if (index1 === index2) {
            log.debug(this, "Not moving/switching columns; the columns are already in the requested positions: %o %o",
                index1, index2);
            return false;
        }

        if (index1 == null || index2 == null) {
            log.debug(this, "Cannot move/switch columns based on the provided info: %o %o", index1, index2);
            return false;
        }

        const column1 = this.columns[index1];
        if (!column1.canBeMoved() && !skipColumnCheck) {
            log.debug(this, "Cannot move/switch columns; the first provided column cannot be moved: %o", column1);
            return false;
        }

        const column2 = this.columns[index2];
        if (!column2.canBeMoved() && !skipColumnCheck) {
            log.debug(this, "Cannot move/switch columns; the second provided column cannot be moved: %o", column2);
            return false;
        }

        return true;
    }

    public findTableRowFromAdditionalTool(button: Button): TableRow {
        return this.rows.find(row => row.additionalToolButtons?.includes(button));
    }

    async deserializeAdditionalRowTools(): Promise<Button[]> {
        if (this.additionalRowToolsDef == null)
            return [];

        const deserializeProps: DeserializeProps = { owner: this.owner, def: this.additionalRowToolsDef, designer: this._designer};
        const comps = await new ComponentDeserializer(deserializeProps).deserialize();
        const result = comps.filter(comp => comp instanceof Button) as Button[];
        this.syncDesignerRowAdditionalToolsPanel(result);
        return result;
    }

    async syncDesignerRowAdditionalToolsPanel(buttons?: Button[]) {
        if (this._designer == null)
            return;
        if (this._additionalRowToolsPanel == null) {
            this._additionalRowToolsPanel = new AdditionalTableRowToolsDesignerPanel(this);
            this.insertIntoTbody(this._additionalRowToolsPanel.wrapperPanel._element);
        }

        if (buttons != null && this._additionalRowToolsPanel.isEmpty())
            this._additionalRowToolsPanel.components = buttons;
    }

    async syncDesignerExpandArea(oldValue) {
        if (this._designer == null)
            return;
        if (this.expandComponentSource == null && this._panelDesignerExpansionDropTarget == null) {
            if (this._designer.allowsDrop) {
                this._panelDesignerExpansionDropTarget = new DropTargetPanel({
                    enclosingComponent: this,
                    _designer: this._designer,
                    backgroundColor: defaultExpandBackground,
                    borderWidth: 1,
                    borderColor: defaultExpandColor,
                    align: HorizontalAlignment.CENTER
                });
                this._panelDesignerExpansionDropTarget.add(new Label({ text: "Drag a component here to start designing the expansion area of this Table's rows", color: "subtle.darker" }));
                if (oldValue != null && this._element.contains(oldValue._element))
                    this._element.removeChild(oldValue._element);
                this._element.appendChild(this._panelDesignerExpansionDropTarget._element);
            }
        }
        else {
            if (this._panelDesignerExpansionDropTarget != null) {
                if (this._element.contains(this._panelDesignerExpansionDropTarget._element))
                    this._element.removeChild(this._panelDesignerExpansionDropTarget._element);
                this._panelDesignerExpansionDropTarget = null;
            }
            if (!(this.expandComponentSource instanceof Component) && (typeof this.expandComponentSource !== "function")) {
                const deserializeProps: DeserializeProps = {owner: this, def: this.expandComponentSource.def, designer: this._designer,dataSources: this.expandComponentSource.dataSources};
                this.expandComponentSource = await new ComponentDeserializer(deserializeProps).deserializeSingleComponent();
                if (this.expandComponentSource.backgroundColor === undefined)
                    this.expandComponentSource.backgroundColor = defaultExpandBackground;
            }
            this._element.appendChild(this.expandComponentSource._element);
        }
    }

    private get designerRow(): TableRow {
        return this._designer != null ? this.rows[0] : null;
    }

    /**
     * Adds an empty row so we can drop components in the table's cells
     */
    private addDesignerRow() {
        if (this._designer != null && !this.isDeserializing() && this.designerRow == null) {
            this.addRow({}, { _designer: this._designer, ...this._rowProps }, { display: true });
        }
    }

    // Allows the child components _boundRow to be set with the correct metadata
    private syncDesignerRow() {
        const designerRow = this.designerRow;
        if (designerRow != null) {
            designerRow.data = new ModelRow(this.dataSource?.url, false);
        }
    }

    clearRows() {
        this.expandRowsPromise = null;
        this.clearDisplayedRows();
        this._allRows = [];
    }

    private clearDisplayedRows() {
        for (const row of this.rows) {
            row._hideExpansionElementSync();
            this._observer.unobserve(row._element);
        }
        this.selectedIndexes = [];
        this.rows = [];
        this._tbody.innerHTML = "";

        this.insertQuickAddRowInternal(false);
        this.layoutAdhocTopRows();

        this.handleEmptyComponent();
    }

    private hideDisplayedRow(row: TableRow) {
        if (row == null)
            return;
        row.expanded = false;
        const rowsIndex = this.rows.indexOf(row);
        if (rowsIndex >= 0) {
            this.rows.splice(rowsIndex, 1);
            this._tbody.removeChild(row._element);
            //remove row from filtered rows so that it matches rows (which it always should),
            //and so that the row count label is updated correctly below
            if (this._filteredRows != null) {
                const filteredRowsIndex = this._filteredRows.indexOf(row);
                if (filteredRowsIndex >= 0)
                    this._filteredRows.splice(filteredRowsIndex, 1);
            }
        }
        this.adjustSelectedIndexesAfterRemoval(rowsIndex);
        this.refreshRowCountPanel();
    }

    private adjustSelectedIndexesAfterRemoval(removedRowIndex: number) {
        if (removedRowIndex == null || removedRowIndex < 0)
            return;

        const rows = this.filteredRows;

        // Remove the index of the removed row from the selectedIndexes array, if it exists.
        // Then, adjust the remaining selected indexes by decrementing those greater than the removed row's index.
        //  If no rows are selected or the table is empty,  reset the selectedIndexes array to ensure there are no invalid selections.
        let updatedSelectedIndexes = this.selectedIndexes.filter(index => index !== removedRowIndex);
        if (rows.length === 0 || this.hasSelection !== true) {
            updatedSelectedIndexes = [];
        } else {
            for (let x = 0; x < updatedSelectedIndexes.length; x++) {
                const sel = updatedSelectedIndexes[x];
                if (removedRowIndex < sel || sel >= rows.length)
                    updatedSelectedIndexes[x] = sel - 1;
            }
        }
        this.selectedIndexes = updatedSelectedIndexes;
    }

    override displayComponentData(rowData: ModelRow, allData: ModelRow[], _rowIndex: number) {
        this.hasDisplayedData = true;
        this.clearDisplayedRows();
        this._allRows = [];
        if (this.field == null)
            this.data = allData;
        else if (rowData != null) {
            this.data = rowData[this.field];
        }
        log.debug(this, "displayData", this, allData);
        if (ArrayUtil.isEmptyArray(this.data)) {
            this.refreshRowCountPanel();
            return;
        }
        const filtered = this._sortRows([...this.filteredRows]);
        for (const row of filtered)
            this.displayRow(row);
        this.refreshRowCountPanel();
        this.applyLastScrollLeft();
    }

    shouldDisplayData(data: ModelRow, allData: ModelRow[], rowIndex: number): boolean {
        return this.dataSource == null || allData == this.dataSource.data
    }

    /**
     * This causes the table to redisplay a given row.  When the underlying data in the table is updated, this method
     * will cause that data to be reflected in the UI.  This has no effect if the Table is virtualized and the user
     * hasn't scrolled to the specified row yet.  When the user does scroll to that row, the most current data will be
     * displayed.
     * @param index The index of the row that needs to be redisplayed.
     */
    redisplaySingleRow(index: number, data: ModelRow, isNewRow: boolean, makeVisible: boolean): void {
        if (isNewRow) {
            const addRowResult = this.addRow(data, null, { display: true, addToData: true, save: false });
            const row = addRowResult.row;
            this.refreshRowCountPanel();
            if (makeVisible == true) {
                row.scrollIntoView();
                setTimeout(() => {
                    row.ripple("primary.lightest", { speed: 300 }).then(() => row.ripple("primary.light", { speed: 500 }));
                }, 500);
            }
        }
        else {
            this.data[index] = data;
            let row = this._allRows[index];
            try {
                const rowIndex = this.getIndexFromData(data, this._allRows);
                if (rowIndex !== -1) {
                    row = this._allRows[rowIndex];
                    row.data = data;
                }
            } catch (e) {
                log.debug(this, "Error redisplaying row: %o", e);
            }

            // If the row has already been populated with its components then we need to refresh its displayed data.
            // - Fire the row display listener so that any logic based on the row's data is evaluated.
            // - Do this even if the row currently isn't displayed to the user (it could be filtered out), but could
            //   become visible to the user again later.  The event logic could also update something the user can see,
            //   such as a count that is visible outside of the table.
            if (row.populatedDOM === true) {
                row.displayComponentValues();
                const displayEvent = new TableRowDisplayEvent(row, this);
                this.fireRowDisplayListeners(displayEvent);
            }

            // Check if the row should be displayed based on the filter
            const rowPassesFilter = this.rowPassesFilter(row);
            const isDisplayed = this.rows.includes(row);

            // Handle row display/reordering based on filter and sort status
            if (isDisplayed) {
                if (!rowPassesFilter) {
                    // Row is currently displayed but no longer passes filter - hide it
                    log.debug(this, "Hiding previously displayed row due to redisplay + filtering: %o", row);
                    this.hideDisplayedRow(row);
                }
                else if (this.isSorted()) {
                    this.resortDisplayedRow(row);
                }
                else {
                    // Row is displayed, passes filter, but table isn't sorted - keep it where it is
                    log.debug(this, "Row being redisplayed will remain in the table: %o", row);
                }
            }
            else if (rowPassesFilter) {
                // Row isn't currently displayed but now passes filter - show it
                const insertPos = this.displayHiddenRow(row);
                log.debug(this, "Previously hidden row being displayed at position %d due to redisplay (now passes filtering): %o", insertPos, row);
            }
            else {
                // Row isn't displayed and doesn't pass filter - keep it hidden
                log.debug(this, "Previously hidden row will remain hidden: %o", row);
            }
        }
    }

    get data(): ModelRow[] {
        return this._data;
    }

    set data(value: ModelRow[]) {
        this._data = value ?? [];
        this._allRows = [];
        this._filteredRows = null;
        const rowMode = this._getRowModeFromDataSource();
        this.data.forEach(dataRow => this.addRow(dataRow, { mode: rowMode }));
        this.initializeSortFromData();
        this.debouncedCheckHeadingPanelToolAvailability();
    }

    private displayHiddenRow(row: TableRow): number {
        if (row == null) {
            return;
        }
        const insertPos = this._getRowInsertPos(row);
        this.displayRow(row, insertPos);
        this._filteredRows?.splice(insertPos, 0, row);
        this.refreshRowCountPanel();
        return insertPos;
    }

    /**
     * Evaluates an already-displayed row's position in the table based on the current sort, and moves it if necessary.
     *
     * This method in intended to be used when the row could move without the user's interaction (handling live updates,
     * etc.).  This method is not intended to be used to move a row within the dataset, when the user drags a row to
     * change its sequence, etc.
     *
     * The row is moved regardless of if it is currently being edited.
     *
     * @param row The table row that should be moved (if necessary)
     */
    private resortDisplayedRow(row: TableRow) {
        if (row == null) {
            return;
        }
        if (!this.isSorted()) {
            log.debug("Table is not sorted, no need to re-sort row");
            return;
        }
        if (row.expanded) {
            log.debug("Table row is expanded, will not be re-sorted");
            return;
        }

        const rowCurrentIndex = this.rows.indexOf(row);
        if (rowCurrentIndex < 0) {
            log.debug("The row being sorted is not currently displayed: %o", row);
            return;
        }

        // Determine the row's new position by seeing where it would be inserted into the current table if it were not
        // already displayed.  If the row's insert position is the same as its current position, do nothing.  Otherwise,
        // move it to its new home.
        const otherRows = [ ...this.rows ];
        otherRows.splice(rowCurrentIndex, 1);
        const rowNewIndex = this._getRowInsertPos(row, otherRows);
        log.debug("The row's new index is: %o", rowNewIndex);
        if (rowNewIndex === rowCurrentIndex) {
            log.debug("The row does not need to be moved");
            return;
        }

        // Save off selected rows so that we can rebuild the selected indexes array later
        const selectedRows = [ ...this.selectedRows ];
        log.debug("Currently selected rows: %o", selectedRows);

        // Remove the row from the row from the array and the DOM
        this.rows.splice(rowCurrentIndex, 1);
        this._tbody.removeChild(row._element);

        // Remove the row from filtered rows so that it matches rows (which it always should), and so that the row count
        // label is updated correctly below.
        if (this._filteredRows != null) {
            const filteredRowsIndex = this._filteredRows.indexOf(row);
            if (filteredRowsIndex >= 0) {
                this._filteredRows.splice(filteredRowsIndex, 1);
            }
        }

        // Display the row in its new position
        log.debug("Displaying row in its new position.  Row: %o  Index:%o", row, rowNewIndex);
        this.displayRow(row, rowNewIndex, false);
        this._filteredRows?.splice(rowNewIndex, 0, row);

        // Reset the even/odd row colors in the affected range.  The range is the lesser of where the row used to be, or
        // where it was re-inserted, to the end of the table.
        this.resetEvenOddRowColors(Math.min(rowCurrentIndex, rowNewIndex));

        // The row count label should still have the same number, but refresh it just in case
        this.refreshRowCountPanel();

        // Rebuild the selected indexes array, and set it directly (without firing any events, etc)
        const newSelectedIndexes = this.buildSelectedIndexesArray(selectedRows).sort((a, b) => a - b);
        log.debug("Setting the table's selectedIndexes: %o", newSelectedIndexes);
        this._selectedIndexes = newSelectedIndexes;

        log.debug(this, "Row re-sorted after data change.  Row: %o  Index %d", row, rowNewIndex);
    }

    /**
     * This method only updates current sort info, but doesn't actually invoke sorting.
     * It assumes that the data is already sorted, and that we only need to make the table match that order.
     */
    private initializeSortFromData() {
        //if user is using a custom config/sort, don't use the sort from the data being displayed
        if (this.configInUse === this.baseConfig) {
            this.clearSort();
            if (ArrayUtil.isEmptyArray(this.dataSource?.orderBy) === false) {
                for (const orderByInfo of this.dataSource.orderBy) {
                    const selector = this.findSortSelectorsForField(orderByInfo.field)?.selector;
                    if (selector != null)
                        this.addSort({ fieldName: orderByInfo.field, sort: orderByInfo.sort, selector: selector });
                }
            }
        }
        //still apply the data-provided sort to the base config, if it doesn't already specify a sort
        //(this method is likely how it will be specified, we just don't need to populate it more than once)
        if (this.baseConfig?.hasOrderByInfo() === false) {
            if (ArrayUtil.isEmptyArray(this.dataSource?.orderBy) === false) {
                for (const orderByInfo of this.dataSource.orderBy) {
                    this.baseConfig.addOrderByInfo({ field: orderByInfo.field, sort: orderByInfo.sort });
                }
            }
        }
    }

    private findSortSelectorsForField(fieldName: string): any {
        if (StringUtil.isEmptyString(fieldName))
            return null;
        for (const column of this.columns) {
            for (const component of column.headingCell?.components) {
                if (component instanceof SortSelector) {
                    if (component.fieldName === fieldName)
                        return { selector: component };
                    const child = component.getChildSelector(fieldName);
                    if (child != null)
                        return { selector: child, parentSelector: component };
                }
            }
        }
        return null;
    }

    private _getRowModeFromDataSource(): TableRowMode {
        if (this.dataSource == null)
            return TableRowMode.NONE;
        switch (this.dataSource.mode) {
            case DataSourceMode.SEARCH:
                return TableRowMode.SEARCH;
            default:
                return TableRowMode.NONE;
        }
    }

    get expandComponentSource(): ExpandComponentSource {
        return this._expandComponentSource;
    }

    set expandComponentSource(value: ExpandComponentSource) {
        const oldValue = this._expandComponentSource;
        this._expandComponentSource = value;
        if (value != null) {
            this._expandComponentSource.parent = this;
        }
        this.syncExpandHandler();
        this.syncDesignerExpandArea(oldValue);
    }

    public expandComponentDefined(): boolean {
        return this.expandComponentSource != null;
    }

    get expandCellAlignment(): LeftOrRightAlignment {
        return this._expandCellAlignment ?? this.getPropertyDefinitions().expandCellAlignment.defaultValue;
    }

    set expandCellAlignment(value: LeftOrRightAlignment) {
        this._expandCellAlignment = value;
        this.syncExpandHandler();
    }

    displayingExpandCellRight(): boolean {
        return this.expandCellAlignment !== Alignment.LEFT;
    }

    get expandHandler(): ExpandHandler {
        return this._expandHandler;
    }

    private set expandHandler(value: ExpandHandler) {
        this._expandHandler = value;
    }

    private syncExpandHandler() {
        if (this.expandComponentDefined() !== true) {
            this.expandHandler?.removeColumn();
            this.expandHandler = null;
        }
        else {
            if (this.expandHandler == null) {
                this.expandHandler = new ExpandHandler(this);
            }
            this.expandHandler.syncColumnPlacement();
        }
    }

    get additionalRowToolsDef() {
        return this._additionalRowToolsDef;
    }

    set additionalRowToolsDef(value) {
        this._additionalRowToolsDef = value;
    }

    get addType(): AddType {
        return this._addType ?? this.getPropertyDefinitions().addType.defaultValue;
    }

    set addType(value: AddType) {
        if (this._addType === value)
            return;
        this._addType = value;
        this.syncAddActions();
    }

    private syncAddActions() {
        if (this.isDeserializing() === true) {
            log.debug("Not syncing add actions, table is still being deserialized");
            return;
        }
        this.headingPanel.syncStandardTools();
        this.syncQuickAddRow();
    }

    get quickAddData(): ModelRow<unknown> | any {
        return this._quickAddData;
    }

    saveQuickAddData() {
        if (this.quickAddRow.validateSimple() !== true)
            return;
        const quickAddData = this.quickAddData;
        const quickAddRow = this.quickAddRow;
        this.shrinkAllRows();
        this.removeQuickAddRow();
        this.addQuickAddRow(false).then(() => {
            const comp = this.quickAddRow.findFocusableComponent();
            if (comp != null) {
                this.quickAddRow.selected = this.rowModeControlType === RowModeControlType.AUTO;
                comp.focus();
            }
        });
        this.addRow(quickAddData, { mode: TableRowMode.NONE }, { display: true, addToData: true, save: false }); //save should have already occurred
        if (this.filterValue != null && !this.tableRowStringSearcher.rowContains(quickAddRow.data, this.filterValue))
            this.resetFilter();
    }

    public get quickAddRowPosition(): QuickAddRowPosition {
        return this._quickAddRowPosition ?? this.getPropertyDefinitions().quickAddRowPosition.defaultValue;
    }

    public set quickAddRowPosition(value: QuickAddRowPosition) {
        if (this.quickAddRowPosition === value) {
            return;
        }
        this._quickAddRowPosition = value;
        this.syncQuickAddRowPosition();
    }

    private syncQuickAddRow() {
        if (this.isQuickAddAllowed() !== true)
            this.removeQuickAddRow();
        else
            this.addQuickAddRow();
    }

    public async resetQuickAddRow() {
        this.removeQuickAddRow();
        this.syncQuickAddRow();
    }

    private syncQuickAddRowPosition() {
        if (this.quickAddRow == null) {
            return;
        }
        if (this.quickAddRowDisplayed()) {
            this._tbody.removeChild(this.quickAddRowElement);
        }
        this.insertQuickAddRowIntoTbody();
    }

    private async addQuickAddRow(updateTableContent: boolean = true): Promise<void> {
        const release = await this.quickAddRowMutex.acquire();
        try {
            await this.addQuickAddRowInternal(updateTableContent);
        }
        finally {
            release();
        }
    }

    private async addQuickAddRowInternal(updateTableContent: boolean): Promise<void> {
        if (this.quickAddRow != null)
            return;
        this._quickAddData = await this._createNewRowData();
        await this.dataSource?.getMetadata();
        this._quickAddRow = TableRow.create(this);
        this.quickAddRow.setDesigner(this.getDesigner());
        this.quickAddRow.owner = this.owner;
        const props: Partial<TableRowProps> = {
            ...this._rowProps,
            id: this.id + "QuickAddTableRow",
            data: this.quickAddData,
            index: 0,
            expanded: false,
            virtualized: false,
            allowEdit: true,
            allowDelete: false
        };
        this.quickAddRow.setProps(props);
        this.quickAddRow.mode = TableRowMode.QUICK_ADD;
        this.quickAddRowElement.tabIndex = 0;
        this.applyRowProps(this.quickAddRow);
        await this.quickAddRow.populateDOM();
        this.quickAddRow.forEveryChildComponent((component: Component) => {
            component.applyDefaultDataValue();
            component.displayData(component.boundRow, null, null);
        });
        this.insertQuickAddRowInternal(updateTableContent);
    }

    protected insertQuickAddRowInternal(updateTableContent: boolean): void {
        if (this.hasQuickAddRow() && !this.quickAddRowDisplayed()) {
            this.insertQuickAddRowIntoTbody();
            if (updateTableContent === true) {
                this.handleEmptyComponent(); //called to make sure we don't show the empty component when quick add is in use
            }
        }
    }

    /**
     * Insert the quick add row into the table body:
     * - If displaying quick add row FIRST, insert it before any normal rows (and after any adhoc top rows, if present)
     * - If displaying quick add row LAST, append to the end of the table body
     */
    private insertQuickAddRowIntoTbody() {
        let insertBeforeElement: Node = null;
        if (this.quickAddRowPosition === QuickAddRowPosition.FIRST) {
            if (this.adhocTopRows != null) {
                for (let x = 0; x < this._tbody.children.length; x++) {
                    const tbodyChild = this._tbody.children[x];
                    if (this.isAdhocTopRow(tbodyChild) !== true) {
                        insertBeforeElement = tbodyChild;
                        break;
                    }
                }
            }
            if (insertBeforeElement == null) {
                insertBeforeElement = this._tbody.firstChild;
            }
        }
        // If showing quick add row last, insertBeforeElement will be null, and the quick add row will be appended to
        // tbody
        this.insertIntoTbody(this.quickAddRow._element, insertBeforeElement);
    }

    private get quickAddRowElement(): HTMLElement {
        return this.quickAddRow?._element;
    }

    private hasQuickAddRow(): boolean {
        return this.quickAddRowElement != null;
    }

    private quickAddRowDisplayed(): boolean {
        return this.hasQuickAddRow() && this._tbody.contains(this.quickAddRowElement);
    }

    async removeQuickAddRow(): Promise<void> {
        const release = await this.quickAddRowMutex.acquire();
        try {
            this.removeQuickAddRowInternal(); // Now synchronous
        } finally {
            release();
        }
    }

    private removeQuickAddRowInternal() {
        if (this.quickAddRow == null && this.quickAddData == null) {
            return;
        }
        if (this.quickAddRowDisplayed()) {
            this._tbody.removeChild(this.quickAddRowElement);
        }
        this._quickAddRow = null;
        this._quickAddData = null;
        this.handleEmptyComponent(); //called to make sure we show the empty component when quick add is not in use
    }

    async _createNewRowData(): Promise<ModelRow | any> {
        if (this.dataSource != null)
            return await this.dataSource.createBlankRow();
        return {};
    }

    deleteRow(row: TableRow) {
        if (row == null || this.allRows.indexOf(row) < 0) {
            return;
        }

        if (row.mode === TableRowMode.UPDATE)
            this.deactivateOutsideClickListener?.();
        this.dataSource._deleteTableRow(row.data, !this.persistChangesImmediately, this.field);
        this.resetSequence(this._allRows); //don't need to reset row indexes here because DataSource._deleteTableRow() redisplayed data in the table
        this._syncRowsDraggable();
    }

    isNormalAddAllowed(): boolean {
        const assocLayoutName = this.getLayoutNameForMode(DataSourceMode.ADD);
        return this.isAnyAddAllowed() &&
            this.addType === AddType.NORMAL &&
            this.userHasPermissionsToAssocLayout(assocLayoutName, CrudDecoratorPermissionsType.ADD);
    }

    isQuickAddAllowed(): boolean {
        if (this.isAnyAddAllowed() && this.addType === AddType.QUICK && this._designer == null) {
            const assocLayoutName = this.getLayoutNameForMode(DataSourceMode.ADD);
            return StringUtil.isEmptyString(assocLayoutName) ? true :
                this.userHasPermissionsToAssocLayout(assocLayoutName, CrudDecoratorPermissionsType.ADD);
        }
        return false;
    }

    private isAnyAddAllowed(): boolean {
        return this.allowAddDisabledByServer !== true &&
            this.addType !== AddType.NOT_ALLOWED &&
            this.dataSource?.mode !== DataSourceMode.SEARCH;
    }

    private userHasPermissionsToAssocLayout(assocLayoutName: string, permItemName: string): boolean {
        // Don't check current user permissions when displaying/serializing the layout in the designer
        return this._designer != null ||
            (assocLayoutName != null && !PermissionsUtil.isUserDeniedLayoutItem(assocLayoutName, permItemName));
    }

    get allowAddDisabledByServer(): boolean {
        return this._allowAddDisabledByServer ?? this.getPropertyDefinitions().allowAddDisabledByServer.defaultValue;
    }

    private set allowAddDisabledByServer(value: boolean) {
        this._allowAddDisabledByServer = value;
        this.syncAddActions();
    }

    public get allowAdvancedSearch(): boolean {
        const propertyValue =
            this._allowAdvancedSearch ?? this.getPropertyDefinitions().allowAdvancedSearch.defaultValue;
        const assocLayoutName = this.getLayoutNameForMode(DataSourceMode.SEARCH);
        return propertyValue &&
            this.userHasPermissionsToAssocLayout(assocLayoutName, CrudDecoratorPermissionsType.SEARCH);
    }

    public set allowAdvancedSearch(value: boolean) {
        this._allowAdvancedSearch = value;
        this.syncAfterDbSearchListener();
        this.headingPanel.syncStandardTools();
        this.resetEmptyComponentBeforeSearch();
        this.resetEmptyComponentAfterSearch();
    }

    public get allowConfig(): boolean {
        return this._allowConfig ?? this.getPropertyDefinitions().allowConfig.defaultValue;
    }

    public set allowConfig(value: boolean) {
        this._allowConfig = value;
        this.headingPanel.syncStandardTools();
    }

    public get allowDelete(): boolean {
        return (this._allowDelete && this._allowDeleteDisabledByServer !== true) ?? this.getPropertyDefinitions().allowDelete.defaultValue;
    }

    public set allowDelete(value: boolean) {
        this._allowDelete = value;
        this.headingPanel.syncStandardTools();
        this.designerRow?.syncToolsCell();
    }

    public get allowDeleteDisabledByServer(): boolean {
        return this._allowDeleteDisabledByServer;
    }

    private set allowDeleteDisabledByServer(value: boolean) {
        this._allowDeleteDisabledByServer = value;
        this.headingPanel.syncStandardTools();
    }

    public get allowDetail(): boolean {
        return this._allowDetail ?? this.getPropertyDefinitions().allowDetail.defaultValue;
    }

    public set allowDetail(value: boolean) {
        if (value !== true && this.rowProps != null)
            delete this.rowProps.onDblClick;
        this._allowDetail = value;
        if (value === true) {
            if (this.rowProps == null)
                this.rowProps = {};
            this.rowProps.onDblClick = (event: ClickEvent) => this.crudManager.showDetailFromClick(event);
        }
        this.headingPanel.syncStandardTools();
    }

    get allowEdit() {
        return (this._allowEdit ?? this.getPropertyDefinitions().allowEdit.defaultValue) &&
            this._allowEditDisabledByServer !== true;
    }

    set allowEdit(value: boolean) {
        this._allowEdit = value;
        this.headingPanel.syncStandardTools();
        this.designerRow?.syncToolsCell();
    }

    get allowEditDisabledByServer(): boolean {
        return this._allowEditDisabledByServer;
    }

    private set allowEditDisabledByServer(value: boolean) {
        this._allowEditDisabledByServer = value;
        this.headingPanel.syncStandardTools();
    }

    public get allowExport(): boolean {
        return this._allowExport ?? this.getPropertyDefinitions().allowExport.defaultValue;
    }

    public set allowExport(value: boolean) {
        this._allowExport = value;
        this.headingPanel.syncStandardTools();
    }

    public get allowPin(): boolean {
        return this._allowPin ?? this.getPropertyDefinitions().allowPin.defaultValue;
    }

    public set allowPin(value: boolean) {
        this._allowPin = value;
        this.headingPanel.syncStandardTools();
    }

    public get allowShare(): boolean {
        return this._allowShare ?? this.getPropertyDefinitions().allowShare.defaultValue;
    }

    public set allowShare(value: boolean) {
        this._allowShare = value;
        this.headingPanel.syncStandardTools();
    }

    get rowModeControlType() {
        return this._rowModeControlType;
    }

    set rowModeControlType(value: RowModeControlType) {
        this._rowModeControlType = value;
    }

    get persistChangesImmediately(): boolean {
        return this.isFirstTableInListLayout || this._persistChangesImmediately === true;
    }

    set persistChangesImmediately(value: boolean) {
        this._persistChangesImmediately = value;
        if (value && this.dataSource != null)
            this.dataSource.preventChangeNotifications = true;
    }

    private get isFirstTableInListLayout(): boolean {
        const layout = this._designer == null ? this.owner : this.getParentLayout();
        return layout instanceof Layout &&
            layout.hasAllowedUse(AllowedUse.LIST) &&
            layout.findComponentByType(Table) === this;
    }

    public get isLookupTable(): boolean {
        return this.owner instanceof Layout &&
            this.owner.hasAllowedUse(AllowedUse.LOOKUP);
    }

    get sequenceField(): string {
        return this._sequenceField;
    }

    set sequenceField(value: string) {
        //this shouldn't be changed after the Table's properties are initially loaded
        this._sequenceField = value;
    }

    protected applyBusy() {
        if (this.dataSource?.mode !== DataSourceMode.UPDATE) {
            if (this.busy && this._busyImage == null) {
                this._busyImage = new Image({
                    name: ImageName.spinner,
                    color: "primary.light",
                    width: "100%",
                    height: 48,
                    marginTop: 16,
                    marginBottom: 16,
                    rotate: true
                });
            }
            this.handleEmptyComponent();
        }
        if (this.busy !== true) {
            this.handleEmptyComponent();
        }
    }

    get emptyCaptionAfterSearch() {
        if (this._emptyCaptionAfterSearch != null || this._designer != null) {
            return this._emptyCaptionAfterSearch;
        }
        const allowsSearching = this.allowDbSearch || this.allowAdvancedSearch;
        const allowsFiltering = this.allowFilter;
        const baseCaption = "No results found.";
        if (allowsSearching && allowsFiltering) {
            return `${baseCaption} Try adjusting your filters or search criteria.`;
        }
        if (allowsSearching) {
            return `${baseCaption} Try adjusting your search criteria.`;
        }
        if (allowsFiltering) {
            return `${baseCaption} Try adjusting your filters.`;
        }
        return baseCaption;
    }

    set emptyCaptionAfterSearch(value: string) {
        this._emptyCaptionAfterSearch = value;
        this.resetEmptyComponentAfterSearch();
    }

    private resetEmptyComponentAfterSearch() {
        this._emptyComponentAfterSearch = this.createEmptyComponentFromCaption(this.emptyCaptionAfterSearch);
        this.replaceEmptyComponent(this.emptyComponentAfterSearch);
    }

    get emptyCaptionBeforeSearch() {
        if (this._emptyCaptionBeforeSearch != null || this._designer != null) {
            return this._emptyCaptionBeforeSearch;
        }
        return "Search using the desired criteria to view results.";
    }

    set emptyCaptionBeforeSearch(value: string) {
        this._emptyCaptionBeforeSearch = value;
        this.resetEmptyComponentBeforeSearch();
    }

    private resetEmptyComponentBeforeSearch() {
        this._emptyComponentBeforeSearch = this.createEmptyComponentFromCaption(this.emptyCaptionBeforeSearch);
        this.replaceEmptyComponent(this.emptyComponentBeforeSearch);
    }

    private ensureEmptyComponentsCreated() {
        if (this.emptyComponentAfterSearch == null) {
            this.resetEmptyComponentAfterSearch();
        }
        if (this.emptyComponentBeforeSearch == null) {
            this.resetEmptyComponentBeforeSearch();
        }
    }

    private createEmptyComponentFromCaption(text: string): Component {
        const newEmptyComponent = new Panel({ fillHeight: true });
        if (!StringUtil.isEmptyString(text)) {
            const emptyLabel = ComponentFactory.createCommon(text, {
                fillRow: true,
                fontSize: "large",
                color: "subtle.light",
                align: HorizontalAlignment.CENTER,
                paddingTop: 12,
                paddingBottom: 12
            });
            newEmptyComponent.add(emptyLabel);
        }
        return newEmptyComponent;
    }

    private get currentEmptyComponent(): Component {
        return this.hasDisplayedData ? this.emptyComponentAfterSearch : this.emptyComponentBeforeSearch;
    }

    get emptyComponentAfterSearch(): Component {
        return this._emptyComponentAfterSearch;
    }

    set emptyComponentAfterSearch(value: Component) {
        if (this.emptyComponentAfterSearch === value) {
            return;
        }
        const oldEmptyComponent = this.currentEmptyComponent;
        this._emptyComponentAfterSearch = value;
        this.replaceEmptyComponent(oldEmptyComponent);
    }

    get emptyComponentBeforeSearch(): Component {
        return this._emptyComponentBeforeSearch;
    }

    set emptyComponentBeforeSearch(value: Component) {
        if (this.emptyComponentBeforeSearch === value) {
            return;
        }
        const oldEmptyComponent = this.currentEmptyComponent;
        this._emptyComponentBeforeSearch = value;
        this.replaceEmptyComponent(oldEmptyComponent);
    }

    private replaceEmptyComponent(oldEmptyComponent: Component) {
        if (oldEmptyComponent != null && this._table.contains(oldEmptyComponent._element)) {
            this._table.replaceChild(this.currentEmptyComponent._element, oldEmptyComponent._element);
        } else {
            this.handleEmptyComponent();
        }
    }

    private handleEmptyComponent() {
        this._noRecordsMatch = false;
        if (this.busy) {
            if (this._busyImage != null) {
                this.setTableContent(this._busyImage._element);
            }
        } else {
            if (this._tbody.childElementCount === 0 && this.currentEmptyComponent != null) {
                this.setTableContent(this.currentEmptyComponent._element);
                this._noRecordsMatch = true;
            }
            else {
                this.setTableContent(this._tbody);
            }
        }
    }

    /**
     * The table element can only have two child elements at any given time.
     * - The table header element.
     * - The table content.  Possible candidates for the table content are:
     *   - The table body
     *   - The busy spinner
     *   - The after search empty component
     *   - The before search empty component
     *
     * This method replaces the currently-displayed table content element with the new content element to display.  It
     * will not affect the table header element.
     *
     * @param element The element to display
     */
    private setTableContent(element: HTMLElement) {
        if (!this._table.contains(element)) {
            for (let x=this._table.children.length-1 ; x>=0 ; x--) {
                const childElement = this._table.children[x];
                if (childElement !== this._thead) {
                    childElement.remove();
                }
            }
            this._table.appendChild(element);
        }
    }

    get noRecordsMatch() {
        return this._noRecordsMatch;
    }

    private applyRowProps(tableRow: TableRow) { // it'd be nice to allow fully setting the row props instead of just a select few
        if (this.rowBorderBottomWidth !== 1)
            tableRow.borderBottomWidth = this.rowBorderBottomWidth;
        if (this.rowBorderBottomColor != null)
            tableRow.borderBottomColor = this.rowBorderBottomColor;
        if (this.rowBorderTopWidth !== 1)
            tableRow.borderTopWidth = this.rowBorderTopWidth;
        if (this.rowBorderTopColor != null)
            tableRow.borderTopColor = this.rowBorderTopColor;
    }

    _applyCellProps(cell: TableCell) {
        if (this.rowAlign == null || this.rowAlign === VerticalAlignment.TOP)
            cell.verticalAlign = null;
        else
            cell.verticalAlign = this.rowAlign;
        if (this.rowSpacing == null || this.rowSpacing === 4) {
            cell.paddingTop = null;
            cell.paddingBottom = null;
        }
        else {
            cell.paddingTop = this.rowSpacing;
            cell.paddingBottom = this.rowSpacing * 2;
        }
    }

    set headerVisible(value) {
        if (value === this._headerVisible)
            return;
        this._headerVisible = value;
        if (value)
            this._element.insertBefore(this.headingPanel._element, this._table);
        else
            this._element.removeChild(this.headingPanel._element);

    }
    get headerVisible() { return this._headerVisible; }

    getComponentsForDiagnostic(): Component[] {
        const result = [this.headingPanel, this.headingRow, ...this.adhocTopRows];
        if (this.quickAddRow != null) {
            result.push(this.quickAddRow);
        }
        result.push(...this.rows);
        return result;
    }

    get rowCount(): number {
        return this._allRows.length;
    }

    get displayedRowCount(): number {
        return this.rows.length;
    }

    get selectedRowCount(): number {
        return this.selectedRows?.length ?? 0;
    }

    private refreshRowCountPanel() {
        this.headingPanel.refreshRowCountPanel();
    }

    addRowExpandListener(value: TableRowExpansionListener) {
        this.addEventListener(_rowExpandListenerDef, value);
    }

    removeRowExpandListener(value: TableRowExpansionListener) {
        this.removeEventListener(_rowExpandListenerDef, value);
    }

    private fireRowExpandListeners(row: TableRow, expandComponent: Component) {
        this.fireListeners(_rowExpandListenerDef, new TableRowExpansionEvent(row, expandComponent, this, row.expanded));
    }

    addRowCollapseListener(value: TableRowExpansionListener) {
        this.addEventListener(_rowCollapseListenerDef, value);
    }

    removeRowCollapseListener(value: TableRowExpansionListener) {
        this.removeEventListener(_rowCollapseListenerDef, value);
    }

    addRowCreateListener(value: TableRowCreationListener) {
        this.addEventListener(_rowCreateListenerDef, value);
    }

    removeRowCreateListener(value: TableRowCreationListener) {
        this.removeEventListener(_rowCreateListenerDef, value);
    }

    fireRowCreationListeners(creationEvent: TableRowCreationEvent) {
        this.fireListeners(_rowCreateListenerDef, creationEvent);
    }

    addRowBeforeSaveListener(value: TableRowBeforeSaveListener) {
        this.addEventListener(_rowBeforeSaveListenerDef, value);
    }

    removeRowBeforeSaveListener(value: TableRowBeforeSaveListener) {
        this.removeEventListener(_rowBeforeSaveListenerDef, value);
    }

    fireRowBeforeSaveListeners(beforeRowSaveEvent: TableRowBeforeSaveEvent) {
        this.fireListeners(_rowBeforeSaveListenerDef, beforeRowSaveEvent);
    }

    addContentsChangedListener(value: TableContentsChangedListener) {
        this.addEventListener(_contentsChangedListenerDef, value);
    }

    removeContentsChangedListener(value: TableContentsChangedListener) {
        this.removeEventListener(_contentsChangedListenerDef, value);
    }

    fireContentsChangedListener(contentsChangedEvent: TableContentsChangedEvent) {
        this.fireListeners(_contentsChangedListenerDef, contentsChangedEvent);
    }

    addFilterChangedListener(value: TableFilterChangedListener) {
        this.addEventListener(_filterChangedListenerDef, value);
    }

    removeFilterChangedListener(value: TableFilterChangedListener) {
        this.removeEventListener(_filterChangedListenerDef, value);
    }

    fireFilterChangedListener(filterChangedEvent: TableFilterChangedEvent) {
        this.fireListeners(_filterChangedListenerDef, filterChangedEvent);
    }

    addBeforeTableRowMoveListener(value: TableRowMoveListener) {
        this.addEventListener(_beforeTableRowMoveListenerDef, value);
    }

    removeBeforeTableRowMoveListener(value: TableRowMoveListener) {
        this.removeEventListener(_beforeTableRowMoveListenerDef, value);
    }

    fireBeforeTableRowMoveListener(tableRowMoveEvent: TableRowMoveEvent) {
        this.fireListeners(_beforeTableRowMoveListenerDef, tableRowMoveEvent);
    }

    addAfterTableRowMoveListener(value: TableRowMoveListener) {
        this.addEventListener(_afterTableRowMoveListenerDef, value);
    }

    removeAfterTableRowMoveListener(value: TableRowMoveListener) {
        this.removeEventListener(_afterTableRowMoveListenerDef, value);
    }

    fireAfterTableRowMoveListener(tableRowMoveEvent: TableRowMoveEvent) {
        this.fireListeners(_afterTableRowMoveListenerDef, tableRowMoveEvent);
    }

    addAfterCrudCloseListener(value: CrudDecoratorCloseListener) {
        this.addEventListener(_afterCrudCoseListenerDef, value);
    }

    removeAfterCrudCloseListener(value: CrudDecoratorCloseListener) {
        this.removeEventListener(_afterCrudCoseListenerDef, value);
    }

    fireAfterCrudCloseListener(crudCloseEvent: CrudDecoratorCloseEvent) {
        this.fireListeners(_afterCrudCoseListenerDef, crudCloseEvent);
    }

    addRowDisplayListener(value: TableRowDisplayListener) {
        this.addEventListener(_rowDisplayListenerDef, value);
    }

    removeRowDisplayListener(value: TableRowDisplayListener) {
        this.removeEventListener(_rowDisplayListenerDef, value);
    }

    fireRowDisplayListeners(displayEvent: TableRowDisplayEvent) {
        this.fireListeners(_rowDisplayListenerDef, displayEvent);
        if (this.removePlaceholderRowDisplayListener === true) {
            this.removeRowDisplayListener(this.setPlaceholderRowDimensions);
            this.removePlaceholderRowDisplayListener = false;
        }
    }

    addRowModeChangeListener(value: TableRowModeChangeListener) {
        this.addEventListener(_rowModeChangeListenerDef, value);
    }

    removeRowModeChangeListener(value: TableRowModeChangeListener) {
        this.removeEventListener(_rowModeChangeListenerDef, value);
    }

    fireRowModeChangeListeners(modeChangeEvent: TableRowModeChangeEvent) {
        this.fireListeners(_rowModeChangeListenerDef, modeChangeEvent);
    }

    addSelectionListener(value: TableSelectionListener) {
        this.addEventListener(_selectionListenerDef, value);
    }

    removeSelectionListener(value: TableSelectionListener) {
        this.removeEventListener(_selectionListenerDef, value);
    }

    private fireSelectionListeners(event: TableSelectionEvent) {
        this.fireListeners(_selectionListenerDef, event);
    }

    addAdditionalRowToolsDisplayListener(value: TableRowAdditionalToolsDisplayListener) {
        this.addEventListener(_rowAdditionalToolsListenersDef, value);
    }

    removeAdditionalRowToolsDisplayListener(value: TableRowAdditionalToolsDisplayListener) {
        this.removeEventListener(_rowAdditionalToolsListenersDef, value);
    }

    fireAdditionalRowToolsDisplayListeners(eventOrRow: TableRowAdditionalToolsDisplayEvent | TableRow) {
        if (eventOrRow instanceof TableRow)
            eventOrRow = new TableRowAdditionalToolsDisplayEvent(eventOrRow, this);
        if (!ArrayUtil.isEmptyArray(eventOrRow.getTableRow()?.additionalToolButtons))
            return this.fireListeners(_rowAdditionalToolsListenersDef, eventOrRow);
    }

    override getPropertyDefinitions() {
        return TablePropDefinitions.getDefinitions();
    }

    async getFields(): Promise<any[]> {
        return this.columns?.map(async column => await column.getFields());
    }

    private get tableRowStringSearcher(): TableRowStringSearcher {
        if (this._tableRowStringSearcher == null)
            this._tableRowStringSearcher = new TableRowStringSearcher(this);
        return this._tableRowStringSearcher;
    }

    /**
     * The filteredRows array represents the current set of rows that match the filter.  The actual object returned could be:
     *  -> the _allRows array, when no valid filter is present, or there are no rows in the table
     *  -> the _filteredRows array, when a valid filter is present
     *
     * In theory the result of this method should match _rows after the act of evaluating the filter is complete.
     * We keep _filteredRows around afterward so that we can use its presence to tell if the filter has already been evaluated
     * (_filteredRows gets nulled out again when the user changes the filter or when the data in the overall table is reset)
     */
    get filteredRows(): TableRow[] {
        if (this._filteredRows == null) {
            if (this._customFilter == null && (this.filterValue == null || this.filterValue.length === 0) || this._allRows.length === 0)
                return this._allRows;
            const filterValue = this.filterValue?.toLowerCase();
            const result = [];
            for (const row of this._allRows) {
                if (this.rowPassesFilter(row, filterValue) === true)
                    result.push(row);
            }
            this._filteredRows = result;
        }
        return this._filteredRows;
    }

    set customFilter(filter:(data:TableRow) => boolean) {
        this._customFilter = filter;
        this.applyFilters();
    }

    get customFilter(){
        return this._customFilter;
    }

    clearCustomFilter() {
        this._customFilter = null;
        this.applyFilters();
    }

    public applyFilters() {
        this._filteredRows = null;
        this._applyFiltering();
        this._syncRowsDraggable();
    }

    private rowPassesFilter(row: TableRow, providedFilterValue?: string): boolean {
        let filterValue: string;
        if (StringUtil.isEmptyString(providedFilterValue) !== true)
            filterValue = providedFilterValue; //assumed to already be in lower case
        else
            filterValue = this.filterValue?.toLowerCase();

        let result = true;
        if (this._customFilter != null && !this._customFilter(row))
            result = false;
        if (StringUtil.isEmptyString(filterValue) === false &&
            !this.tableRowStringSearcher.rowContains(row.data, filterValue)) {
            result = false;
        }
        return result;
    }

    /**
     * This method allows access to the Table's addlSearcherCallback function.  It is intended to be used primarily by
     * TableRowStringSearcher, which compares the Table's input filter value against data in each TableRow.
     */
    get addlSearcherCallback(): () => ComponentSearcher[] {
        return this._addlSearcherCallback;
    }

    /**
     * The addlSearcherCallback method provides a way for a Table to make non-standard fields (those bound to a field
     * within a table cell in the layout's definition file) searchable via the Table's filter field.
     *
     * For example, say a component is added in code and is not present in the table's definition in the layout file.
     * By default, the filter logic would have no way to match any value presented in that component.  But by specifying the
     * addlSearcherCallback, such a component could be made searchable.
     *
     * The panelBrkStatus component in BrokerageMovementTable.ts is such an example: it is defined as a Panel in the layout,
     * and is not bound to a field.  The Table object in BrokerageMovementTable can define the callback method so that the
     * brokerage status code and brokerage status description values are included in the Table's filter.
     *
     * The callback method should return an array of ComponentSearcher objects.  A ComponentSearcher is an object whose searcher function
     * takes the relevant ModelRow as a parameter and returns a ComponentSearchResult object that can be used in filter comparisons.
     *
     * So in the previous Brokerage Planning example, 2 ComponentSearchers are returned:
     *
     *     tableSearcherCreationCallback(): ComponentSearcher[] {
     *       return [
     *         new ComponentSearcher("brokerage_status"),
     *         new ComponentSearcher("brokerage_status_descr")
     *       ];
     *     }
     *
     * The simplest version of ComponentSearcher takes the single fieldName parameter described above.  Other parameters, which include a
     * fieldName alias and a custom searcher fuction, are available.
     */
    set addlSearcherCallback(value: () => ComponentSearcher[]) {
        this._addlSearcherCallback = value;
    }

    _applyFiltering() {
        this.clearDisplayedRows();
        this.refreshRowCountPanel();
        let filteredRows = this.filteredRows;
        if (filteredRows == null) {
            return;
        }
        filteredRows = this._sortRows([...filteredRows]);
        this.resetIndexAndSequence(filteredRows, true, false);
        for (const row of filteredRows) {
            row.expanded = false;
            this.displayRow(row);
        }
        this.applyLastScrollLeft();
        this.fireFilterChangedListener(new TableFilterChangedEvent(this, this.filterValue));
        this.headingPanel.checkToolAvailability();
    }

    getSearchValues(): string[] {
        const result = [];
        for (const row of this.rows) {
            result.push(...row.getSearchValues());
        }
        return result;
    }

    getParentDataSource(): DataSource {
        let result = null;
        if (this.parent instanceof Tab) {
            result = this.parent.dataSource;
        }
        return result;
    }

    dataSourceModeChanged(mode: DataSourceMode): void {
        super.dataSourceModeChanged(mode);
        this.headingPanel.visible = this.headerVisible && mode !== DataSourceMode.SEARCH;
        if (mode === DataSourceMode.NONE) {
            this.headingPanel.syncSearchFilterFields();
        }
        this.syncAddActions();
        if (mode === DataSourceMode.SEARCH) {
            this.clearRows();
        }
    }

    completeEditedRows(): boolean {
        let result = true;
        for (const row of this.rows) {
            if (row.rowBeingEdited()) {
                row.shrinkAllComponents();
                result = result && row.saveChanges();
            } else if (row.isSearchRow() && row.selected) {
                this.clearSelectedRows();
            }
        }
        if (this.quickAddRow != null)
            this.quickAddRow.selected = false;
        return result;
    }

    get allowDbSearch(): boolean {
        return this._allowDbSearch ?? this.getPropertyDefinitions().allowDbSearch.defaultValue;
    }

    set allowDbSearch(value: boolean) {
        this._allowDbSearch = value;
        this.syncAfterDbSearchListener();
        this.headingPanel.syncSearchFilterFields();
        this.headingPanel.refreshTextboxSearchTooltip();
        this.resetEmptyComponentBeforeSearch();
        this.resetEmptyComponentAfterSearch();
    }

    private syncAfterDbSearchListener() {
        if (this.allowDbSearch === true || this.allowAdvancedSearch === true)
            this.dataSource?.addAfterExecutionListener(this.doAfterDbSearch);
        else
            this.dataSource?.removeAfterExecutionListener(this.doAfterDbSearch);
    }

    private doAfterDbSearch = (event: DataSourceExecutionEvent) => {
        if (event.getAction() === DataSourceAction.SEARCH && event.isAfter())
            this.headingPanel.doAfterDbSearch();
    }

    get allowFilter(): boolean {
        return this._allowFilter ?? this.getPropertyDefinitions().allowFilter.defaultValue;
    }

    set allowFilter(value: boolean) {
        this._allowFilter = value;
        this.headingPanel.syncSearchFilterFields();
        this.resetEmptyComponentBeforeSearch();
        this.resetEmptyComponentAfterSearch();
    }

    public buildConfig(): TableConfig {
        const result = new TableConfig();
        result.tableId = this.id;
        result.layoutPath = this.getRootLayout()?.layoutName;
        for (const column of this.columns) {
            if (column.canBeMoved()) {
                result.addColumn({ headingCellId: column._headingCell.id });
            }
        }
        if (this._sortInfo != null) {
            for (const fieldSortInfo of this._sortInfo) {
                result.addOrderByInfo({ field: fieldSortInfo.fieldName, sort: fieldSortInfo.sort });
            }
        }
        return result;
    }

    get configInUse(): TableConfig {
        return this._configInUse;
    }

    set configInUse(value: TableConfig) {
        if (value == null || this.configInUse?.id === value.id) {
            return;
        }
        //store default configuration so we can revert back to it if necessary
        if (this._baseConfig == null)
            this._baseConfig = this.buildConfig();
        //remove columns but hold them as variables
        //add columns back based on order from config
        //each addColumn() call in turn calls setupColumnSorting() for that column
        //add sort similar to how we call it in _initializeSortFromData()
        log.debug(this, "Applying table configuration %o", value);
        this._configInUse = value;
        const currentColumns = [ ...this.getNormalColumns() ];
        let missingColumns = [ ...this.getNormalColumns() ];
        this.clearColumns();
        let index = -1;
        for (const columnDescriptor of value.columnDescriptors) {
            index++;
            for (const currentColumn of currentColumns) {
                if (columnDescriptor.headingCellId === currentColumn.headingCell.id) {
                    log.debug(this, "Adding column %o", columnDescriptor);
                    this.addColumn(currentColumn);
                    missingColumns = missingColumns.filter(column => columnDescriptor.headingCellId != column.headingCell.id);
                    break;
                }
            }
        }

        missingColumns.forEach((column) => {
            log.debug(this, "Adding column that was not defined in the table configuration %o", column);
            this.addColumn(column);
        });
        this.clearSort();
        for (const orderByInfo of value.orderByInfo) {
            const selectors = this.findSortSelectorsForField(orderByInfo.field);
            if (selectors != null) {
                log.debug(this, "Applying order by %o to selectors %o", orderByInfo, selectors);
                this.sortByField(true, selectors.selector, selectors.parentSelector, orderByInfo.sort);
            }
            else {
                const message = "Sort Selector not found for order by %o, the field may no longer be in the layout";
                log.debug(this, message, orderByInfo);
            }
        }
    }

    /**
     * This method can be used to apply the user's default table configuration.  Normally the default configuration is
     * applied after the containing layout has been deserialized (and it's more efficient to do it then).  This method
     * exists to provide us the ability to reset back to the default configuration, which is helpful when the id of an
     * already-deserialized table changes (think Brokerage Planning).
     */
    public applyDefaultConfig(layoutName?: string) {
        if (this.isDeserializing() === true) {
            log.debug(this, "Not applying default table configuration; the table is still being deserialized");
            return;
        }
        const layoutPath = StringUtil.isEmptyString(layoutName) === false ? layoutName :
            this.getParentLayout()?.layoutName;
        if (StringUtil.isEmptyString(layoutPath) === true) {
            log.debug(this, "Not applying default table configuration; the parent layout is not known");
            return;
        }
        const defaultConfigRow = UserSettings.getSingleton().getDefaultTableConfigRow(layoutPath, this.id);
        log.debug(this, "Found default table configuration %o", defaultConfigRow);
        if (defaultConfigRow != null)
            this.configInUse = new TableConfig(defaultConfigRow);
    }

    get baseConfig(): TableConfig {
        return this._baseConfig;
    }

    public useBaseConfig() {
        this.configInUse = this.baseConfig;
    }

    validate(checkRequired: boolean, showErrors: boolean = true): ValidationResult[] {
        let result: ValidationResult[] = null;
        for (const row of this.rows) {
            const thisResult: ValidationResult[] = row.validate(checkRequired, showErrors);
            if (thisResult !== null) {
                if (result == null)
                    result = thisResult;
                else
                    result = result.concat(thisResult);
            }
        }

        if (this.onValidate != null) {
            const onValidateResult = this.onValidate(this);
            if (result == null)
                result = onValidateResult;
            else
                result = result.concat(onValidateResult);
        }

        //if all rows are valid, quit editing them
        if (result != null) {
            let allRowsValidationResult = true;
            for (const rowValidationResult of result) {
                if (rowValidationResult.isValid !== true) {
                    allRowsValidationResult = false;
                    break;
                }
            }
            if (allRowsValidationResult === true) {
                this.completeEditedRows();
            }
        }
        else //a null ValidationResult[] object means everything was valid
            this.completeEditedRows();
        return result;
    }

    override get serializationName() {
        return "table";
    }

    override get properName(): string {
        return "Table";
    }

    private _syncRowsDraggable() {
        const value = this.rowsAreDraggable();
        for (const row of this._allRows) {
            row.draggable = value;
        }
    }

    rowsAreDraggable(): boolean {
        return StringUtil.isEmptyString(this._sequenceField) !== true && this._allRows.length > 1 && this.rowsDisplayedInSequence() === true;
    }

    private rowsDisplayedInSequence(): boolean {
        //if the table has been filtered, we probably aren't looking at all of the possible rows, so assume rows are not all present and in sequence
        if (this.isFiltered() === true)
            return false;
        //if the rows are sorted, see how they are sorted.  if they are sorted only by the sequence field (in ascending order), then they are in sequence
        if (this.isSorted() === true) {
            for (const si of this._sortInfo) {
                if (si.fieldName !== this._sequenceField || si.sort !== "asc")
                    return false;
            }
        }
        return true;
    }

    /**
     * Moves a TableRow from one index to another
     * This method assumes:
     *    - no filter criteria is present
     *    - the table has not been sorted by the user (via the column headers)
     *
     * @param row: the TableRow object to move
     * @param newIndex: the index to which the TableRow should be moved
     * @param oldIndex: the index from which the TableRow originated
     * @param dragSession: the DragSession created related to the TableRow drag event
     */
    moveRow(row: TableRow, newIndex: number, dragSession?: DragSession) {
        const oldIndex = row.index;
        const beforeMoveEvent = new TableRowMoveEvent(this, row, newIndex, oldIndex, true, dragSession);
        this.fireBeforeTableRowMoveListener(beforeMoveEvent);
        // Clear top values that were set during dragging
        this.clearMoveTopValues();
        if (beforeMoveEvent.defaultPrevented === true) {
            log.debug(this, "Move of row cancelled: row: %o, oldIndex: %o, newIndex: %o", row, oldIndex, newIndex);
            return;
        }
        if (row.index === newIndex || newIndex < 0 || newIndex > this._rows.length - 1) {
            return;
        }
        ArrayUtil.moveArrayElement(this.data, row.index, newIndex);
        ArrayUtil.moveArrayElement(this._rows, row.index, newIndex);
        ArrayUtil.moveArrayElement(this._allRows, row.index, newIndex);
        this.resetIndexAndSequence(this._allRows);
        this.moveRowInDOM(row, newIndex);
        const afterMoveEvent = new TableRowMoveEvent(this, row, newIndex, oldIndex, false, dragSession);
        this.fireAfterTableRowMoveListener(afterMoveEvent);
    }

    private clearMoveTopValues() {
        log.debug(this, "Removing top values applied during row dragging");
        this.rows.forEach(row => row._element.style.top = "");
    }

    private resetSequence(rows: TableRow[]) {
        this.resetIndexAndSequence(rows, false, true);
    }

    /**
     * Sets each TableRow's sequenceField and index values
     * The sequenceField starts its count at 1, while the index starts at zero
     *
     * @param rows: the TableRow[] containing rows to resequence
     */
    private resetIndexAndSequence(rows: TableRow[], resetIndex: boolean = true, resetSequence: boolean = true) {
        const needToResetSequence = this.dataSource?.mode !== DataSourceMode.SEARCH &&
            resetSequence === true &&
            StringUtil.isEmptyString(this.sequenceField) !== true;
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (resetIndex === true) {
                row.index = i;
            }
            if (needToResetSequence === true && row.data instanceof ModelRow) {
                row.data.set(this.sequenceField, i + 1, this);
            }
        }
    }

    /**
     * Moves a TableRow's element within the tbody's children
     *
     * @param row The TableRow to move
     * @param newIndex The row's new index.  Note that the newIndex value should not take the quick add row into
     *                  account.  The method will adjust the newIndex value if necessary (when the quick add row is
     *                  present).
     */
    private moveRowInDOM(row: TableRow, newIndex: number) {
        this._tbody.removeChild(row._element);
        const adhocTopRowsOffset = this.adhocTopRowCount;
        const quickAddRowOffset =
            (this.quickAddRowDisplayed() && this.quickAddRowPosition === QuickAddRowPosition.FIRST) ? 1 : 0;
        const adjustedIndex = newIndex + adhocTopRowsOffset + quickAddRowOffset;
        this.insertIntoTbody(row._element, this._tbody.children[adjustedIndex])
    }

    /**
     * Sets the onmousemove event on the _tbody element
     *
     * @param fn: the listener method (can be null)
     */
    setTableBodyOnMouseMove(fn: (event: DomMouseEvent) => void) {
        this._tbody.onmousemove = fn;
    }

    /**
     * Defines if the component can grow to the height of the container that contains it.
     * Table's implementation of this method sets the table's maxHeight to the provided value.
     * This allows the table to fill up the container, but still get a scroll bar when more records are present than can be viewed in that height.
     *
     * @param height: the height to use to reset the table's maxHeight (provided as a number)
     */
    public override growToContainerHeight(height: number) {
        this.maxHeight = height;
    }

    override getListenerDefs(): Collection<ListenerListDef> {
        return {
            ...super.getListenerDefs(),
            "rowExpand": { ..._rowExpandListenerDef },
            "rowCreate": { ..._rowCreateListenerDef },
            "contentsChanged": { ..._contentsChangedListenerDef },
            "filterChanged": { ..._filterChangedListenerDef },
            "afterTableRowMove": { ..._afterTableRowMoveListenerDef },
            "beforeTableRowMove": { ..._beforeTableRowMoveListenerDef },
            "afterCrudClose": { ..._afterCrudCoseListenerDef },
            "rowBeforeSave": { ..._rowBeforeSaveListenerDef },
            "rowDisplay": { ..._rowDisplayListenerDef },
            "rowModeChange": { ..._rowModeChangeListenerDef },
            "selection": { ..._selectionListenerDef }
        };
    }

    private getLayoutNameForMode(mode: DataSourceMode) {
        switch (mode) {
            case DataSourceMode.SEARCH: return this.searchLayout;
            case DataSourceMode.ADD: return this.addLayout;
            case DataSourceMode.UPDATE: return this.editLayout;
            default: return this.generalLayout;
        }
    }

    public get generalLayout(): string {
        return this._generalLayout;
    }

    public set generalLayout(value: string) {
        this._generalLayout = value;
        this.resetEmptyComponentBeforeSearch();
        this.resetEmptyComponentAfterSearch();
        this.headingPanel.syncStandardTools();
    }
    public get detailLayout(): string {
        return this._detailLayout || this._generalLayout;
    }

    public set detailLayout(value: string) {
        this._detailLayout = value;
        this.headingPanel.syncStandardTools();
    }

    public get addLayout(): string {
        return this._addLayout || this._generalLayout;
    }

    public set addLayout(value: string) {
        this._addLayout = value;
        this.headingPanel.syncStandardTools();
    }

    public get editLayout(): string {
        return this._editLayout || this._generalLayout;
    }

    public set editLayout(value: string) {
        this._editLayout = value;
        this.headingPanel.syncStandardTools();
    }

    public get searchLayout(): string {
        return this._searchLayout || this._generalLayout;
    }

    public set searchLayout(value: string) {
        this._searchLayout = value;
        this.resetEmptyComponentBeforeSearch();
        this.resetEmptyComponentAfterSearch();
        this.headingPanel.syncStandardTools();
    }

    public override get dataSource(): DataSource<ModelRow<any>> {
        return super.dataSource;
    }

    public set dataSource(value: DataSource<ModelRow<any>>) {
        super.dataSource = value;
        if (this.persistChangesImmediately)
            value.preventChangeNotifications = true;
        this.syncDesignerRow();
    }

    public get quickAddRow(): TableRow {
        return this._quickAddRow;
    }

    /**
     * Table's override of updateBoundData is only intended to retrieve search values from the first row in the table
     * when in search mode
     *
     * @param row
     * @param mode
     * @param fillingLinkedValues
     * @returns void
     */
    public override updateBoundData(row: ModelRow, mode: DataSourceMode, fillingLinkedValues?: boolean) {
        if (mode !== DataSourceMode.SEARCH || fillingLinkedValues !== true)
            return;
        const firstRowInTable = this.rows[0]?.data;
        if (firstRowInTable == null || !(firstRowInTable instanceof ModelRow))
            return;
        this.rows[0].updateDataFromComponents();
        for (const field of Object.keys(firstRowInTable.data)) {
            const value = firstRowInTable.get(field);
            if (value != null)
                row.set(field, value);
        }
        //row.setValues(this.rows[0]?.data?.data);
    }

    public getBodyHeight(): number {
        return DOMUtil.getElementHeight(this._tbody);
    }

    protected bodyScrolled(_event: DomEvent) {
        const left = this._tbody.scrollLeft;
        if (this.lastScrollLeft !== left) {
            // we need to somehow adjust for the scrollbar in tbody, but I need more math to figure out how
            //      const scrollOffset = DOMUtil.isScrollbarVisible(this._tbody) ? DOMUtil.getScrollbarWidth() : 0;
            this._thead.scrollLeft = left;
            this.lastScrollLeft = left;
        }
    }

    private headScrolled(_event: DomEvent) {
        const left = this._thead.scrollLeft;
        if (this.lastScrollLeft !== left) {
            this._tbody.scrollLeft = left;
            this.lastScrollLeft = left;
        }
    }

    /**
     * The returned value is unreliable until the table has been rendered.
     */
    public get isHorizontallyScrollable(): boolean {
        if (!this._thead || !this._tbody || !this._table) {
            return false;
        }
        const tableWidth = this._thead.scrollWidth;
        const containerWidth = this._table.clientWidth;
        return tableWidth > containerWidth;
    }

    applyLastScrollLeft() {
        const left = this.lastScrollLeft ?? 0;
        this.setElementScrollLeft(this._thead, left);
        this.setElementScrollLeft(this._tbody, left);
    }

    private setElementScrollLeft(element: HTMLElement, left: number) {
        if (element != null && element.scrollLeft !== left) {
            element.scrollLeft = left;
        }
    }

    get permissionsTypes(): PermissionsDefinition[] {
        const result = super.permissionsTypes;
        if (this.isAnyAddAllowed()) {
            result.push({
                permsType: "tableAdd",
                description: "Add security",
                availableToAllDescription: "Everyone can add rows to this table",
                availableToNoneDescription: "No one can add rows to this table"
            });
        }
        if (this.allowEdit) {
            result.push({
                permsType: "tableEdit",
                description: "Edit security",
                availableToAllDescription: "Everyone can edit rows in this table",
                availableToNoneDescription: "No one can edit rows in this table"
            });
        }
        if (this.allowDelete) {
            result.push({
                permsType: "tableDelete",
                description: "Delete security",
                availableToAllDescription: "Everyone can delete rows from this table",
                availableToNoneDescription: "No one can delete rows from this table",
            });
        }
        return result;
    }

    public async resolveRowEdit(originalRow: ModelRow, updatedRow: ModelRow) {
        switch (this.rowEditResolveMode) {
            case TableRowEditResolveMode.NONE: updatedRow = null; break;
            case TableRowEditResolveMode.CUSTOM: updatedRow = await this.doOnResolveRowEdit(updatedRow); break;
            case TableRowEditResolveMode.LOOKUP_KEY: updatedRow = await this.lookupRow(updatedRow.getKeyData());
        }
        if (updatedRow != null)
            this.dataSource.replaceRow(originalRow, updatedRow);
        this.headingPanel.checkToolAvailability();
    }

    private async lookupRow(keyData: Collection<unknown>) {
        const metadata = await this.dataSource.getMetadata();
        const searchParam = {};
        for (const field of metadata.keyFields)
            searchParam[field] = keyData[field];
        searchParam["_field_list"] = { layoutName: this.owner?.layoutName };
        const results = await Model.search(this.dataSource.url, searchParam);
        return results.getSingleModelRow();
    }

    /**
     * Reassign this method to perform custom mapping of updated rows back to the Table's rows.  For example,
     * a Table might have a flattened view of orders and stops.  The layout used to edit it might have a completely
     * different view of the order/stop relationship (one-to-many, or may not even contain the stop join).  This method
     * allows the app-level code to determine how to create a ModelRow that's appropriate for the Table, given the
     * ModelRow that is edited.
     * @param updatedRow
     * @returns
     */
    public async doOnResolveRowEdit(updatedRow: ModelRow): Promise<ModelRow> {
        return updatedRow;
    }

    public get printableToggleEnabled(): boolean {
        return this._printableToggleEnabled == null ? true : this._printableToggleEnabled;
    }

    public set printableToggleEnabled(value: boolean) {
        this._printableToggleEnabled = value;
    }

    override getKeyHandlers(): KeyHandler[] {
        return [{
            element: this._element,
            key: "ALL",
            listener: (event: KeyEvent) => this._handleKeys(event),
            scope: this._element
        }];
    }

    private _handleKeys(event: KeyEvent) {
        // Set the event's shouldAutomaticallyStopPropagation flag back to false; it should only be flipped to true if
        // at least one TableRow's key handler takes action on the event
        log.debug(this, "Attempting to handle key event %o within Table %o", event, this.id);
        event.shouldAutomaticallyStopPropagation = false;
        const activeElement = document.activeElement;
        //handle the key if we are within the quick add row or ir the quick add row is selected
        if (this.quickAddRow != null && DOMUtil.isOrContains(this.quickAddRowElement, activeElement)) {
            log.debug(this, "The quick info row will handle the key");
            this.quickAddRow.handleKey(event);
            return;
        }
        // If the focus is within a component in a particular table row handle the key for that row.
        // Note that when a row is selected and focus is not in a particular component, the active element is the table
        // body element.  Or, when a particular component has focus, its element will be the active element.
        if (activeElement !== this._tbody) {
            for (const row of this.rows) {
                if (DOMUtil.isOrContains(row._element, activeElement)) {
                    log.debug(this, "Row %o will handle the key", row);
                    row.handleKey(event);
                    return;
                }
                const expansion = row.getExpansionComponent();
                if (expansion != null && expansion instanceof Panel && DOMUtil.isOrContains(expansion._element, activeElement)) {
                    log.debug(this, "The expansion area of row %o will handle the key", row);
                    expansion.fillKeyHandlerGroup();
                    expansion.getKeyHandlerGroup().handleKey(event);
                    return;
                }
            }
            this.headingPanelHandleKey(event);
        }
        // If the active element is the table body itself, then rows may or may not be selected
        // If any rows are selected then handle the key for each selected row
        else {
            let keyHandledByRow = false;
            for (const row of this.rows) {
                if (row.selected) {
                    log.debug(this, "Selected row %o will attempt to handle the key", row);
                    const thisRowHandledKey = row.handleKey(event);
                    keyHandledByRow = keyHandledByRow || thisRowHandledKey;
                }
            }

            // If we still haven't handled the key, see if the header panel can handle it
            if (keyHandledByRow !== true)
                this.headingPanelHandleKey(event);
        }
    }

    headingPanelHandleKey(event: KeyEvent) {
        log.debug(this, "Giving the table heading panel an attempt to handle the key");
        this.headingPanel.fillKeyHandlerGroup();
        const keyHandledByTools = this.headingPanel.getKeyHandlerGroup().handleKey(event);
        if (keyHandledByTools === true)
            log.debug(this, "The table heading panel handled the key");
    }

    protected override getBorderPropTargets(_changedProperty: string) {
        return [ this._table ];
    }

    public override doBeforeComponentEnlarge(rowsToEnlarge: TableRow[]) {
        const currHeight = DOMUtil.getElementHeight(this._element);
        if (rowsToEnlarge.includes(this.quickAddRow) !== true && this.quickAddRowDisplayed()) {
            this._tbody.removeChild(this.quickAddRowElement);
        }
        for (const row of this.rows) {
            if (row.expanded === true)
                this._tbody.removeChild(row.expansionElement);
            this._tbody.removeChild(row._element);
        }
        for (const row of rowsToEnlarge) {
            this._tbody.appendChild(row._element);
            if (row.expanded === true)
                this._tbody.appendChild(row.expansionElement);
        }

        // Disable things that would affect the dataset that the table is displaying (so that the dataset cannot change
        // while the user is only viewing one row from that dataset)
        // - search/filter fields, the advanced search button, really all the tools in the table header
        // - sort controls
        const tempTableProps: Partial<TableProps> = { minHeight: Math.max(200, currHeight) };
        const tempHeadingProps: Partial<PanelProps> = { height: DOMUtil.getElementHeight(this.headingPanel._element) };
        this.applyTempState(tempTableProps);
        const tableHeadingState = this.headingPanel.applyTempState(tempHeadingProps);
        tableHeadingState.addObject("components", [...this.headingPanel.components]);
        this.headingPanel.removeAll();
        this.forEverySortSelector((sortSelector: SortSelector) => sortSelector.enabled = false);
    }

    public override doAfterComponentsShrink() {
        for (let x = this._tbody.children.length - 1; x >= 0; x--) {
            this._tbody.removeChild(this._tbody.children[x]);
        }
        for (const row of this.rows) {
            this._tbody.appendChild(row._element);
            if (row.expanded === true)
                this._tbody.appendChild(row.expansionElement);
        }
        this.insertQuickAddRowInternal(false);
        //re-enable the things that affect the dataset that the table is displaying
        // - search/filter fields, the advanced search button, really all the tools in the table header
        // - sort controls
        this.revertTempState();
        const tableHeadingState = this.headingPanel.revertTempState();
        this.headingPanel.add(...tableHeadingState.getObject("components"));
        this.forEverySortSelector((sortSelector: SortSelector) => sortSelector.enabled = true);
    }

    private shrinkAllRows() {
        if (this.quickAddRow != null)
            this.quickAddRow.enlarged = false;
        for (const row of this.rows) {
            row.enlarged = false;
        }
    }

    public override discoverIncludedComponents(): Component[] {
        const cells = this.rows?.[0]?.cells ?? [];
        const headingCells = cells.map(cell => cell.col?.headingCell);
        const result =  [
            ...cells,
            ...headingCells,
            this.expandComponentSource,
            ...this.headingPanel.customTableToolsPanels,
            this._additionalRowToolsPanel
        ];
        return result.filter(c => c != null);
    }

    public get backgroundColor(): Color {
        return super.backgroundColor;
    }

    public set backgroundColor(value: Color) {
        super.backgroundColor = value;
        if (this.headingRow.backgroundColor == null)
            this.headingRow.backgroundColor = value;
    }

    public enableAllRowComponents(value: boolean) {
        const callback = (row: TableRow) => row.enableAllComponents(value);
        this.forEveryRow(false, callback);
    }

    public set onBodyMounted(callback: MountCallback) {
        MountUtil.addMountListener(this._tbody, callback);
    }

    public set onBodyMutation(callback: () => void) {
        MountUtil.addMountListener(this._tbody, callback);
    }

    public addToBodyWithAbsolutePosition(component: Component, left: number|string, top: number|string) {
        const componentInitiallyVisible = component.visible;
        this._tbody.style.position = "relative";
        component.visible = false;
        component.style.position = "absolute";
        component.left = left
        component.top = top;
        this._tbody.appendChild(component._element);
        if (componentInitiallyVisible === true)
            component.visible = true;
    }

    /**
     * This method is called after the outermost layout (the layout that isn't nested) is loaded.  This is helpful as it
     * allows the add actions to not be displayed until after the onLoad of the layout is called.  This allows for any
     * type of logic to be run before the add options (most notably the quick add row) is created.
     */
    override _doAfterOutermostLayoutLoaded() {
        this.syncAddActions()
    }

    protected override determinePropertyDefaultValue(prop: ComponentPropDefinition): any {
        if (prop.name === "filterAlignment") {
            if (this.searchFilterMode === SearchFilterMode.COLLAPSED)
                return Alignment.RIGHT;
        } else if (prop.name === "persistChangesImmediately") {
            return this.isFirstTableInListLayout;
        } else if (["addLayout", "detailLayout", "editLayout", "searchLayout"].includes(prop.name)) {
            return this.generalLayout;
        }
        return super.determinePropertyDefaultValue(prop);
    }

    override styleForTooltip() {
        this._thead.style.backgroundColor = getThemeForKey("table.tooltip")?.backgroundColor;
    }

    get displayingInViewMode(): boolean {
        return this._displayingInViewMode;
    }

    private set displayingInViewMode(value: boolean) {
        this._displayingInViewMode = value;
    }

    override displayInViewMode() {
        this.displayingInViewMode = true;
        this.addType = AddType.NOT_ALLOWED;
        this.additionalRowToolsDef = null;
        this.allowDbSearch = false;
        this.allowDelete = false;
        this.allowEdit = false;
        this._allRows?.forEach(row => row.displayInViewMode());
    }

    public set defaultOpenType(value: OpenType) {
        this.crudManager.defaultOpenType = value;
    }

    public setOpenContextMenuItemOverride(openType: OpenType, method: (row: TableRow) => Partial<ContextMenuLabelProps>)
    {
        this.crudManager.setOpenContextMenuItemOverride(openType, method);
    }

    public set overrideUrlParams(value: (tableRow?: TableRow) => any) {
        this.crudManager.overrideUrlParams = value;
    }

    public set determineDetailKeyValue(value: (tableRow: TableRow) => string) {
        this.crudManager.determineDetailKeyValue = value;
    }

    private forEveryRow(includeHeaderRow: boolean, callback: (row: TableRow) => void) {
        if (includeHeaderRow === true) {
            callback(this.headingRow);
        }
        this.allRows?.forEach(row => callback(row));
        this.adhocTopRows?.forEach(row => callback(row));
        if (this.quickAddRow != null) {
            callback(this.quickAddRow);
        }
    }

    private insertIntoTbody(element: HTMLElement, insertBeforeElement?: Node) {
        if (element === this._additionalRowToolsPanel?.wrapperPanel._element) {
            this._tbody.appendChild(element);
            return;
        }
        if (insertBeforeElement != null) {
            this._tbody.insertBefore(element, insertBeforeElement);
            return;
        }
        if (this.quickAddRowPosition === QuickAddRowPosition.LAST &&
            this.hasQuickAddRow() &&
            this.quickAddRowDisplayed()) {
            this._tbody.insertBefore(element, this.quickAddRow._element);
            return;
        }
        if (this._tbody.contains(this._additionalRowToolsPanel?.wrapperPanel._element)) {
            this._tbody.insertBefore(element, this._additionalRowToolsPanel.wrapperPanel._element);
            return;
        }
        this._tbody.appendChild(element);
    }

    get designerToolsColumn(): TableColumn {
        return this.columns.find(column => this.isDesignerToolsColumn(column));
    }

    get expandColumn(): TableColumn {
        return this.columns.find(column => this.isExpandColumn(column));
    }

    get selectCheckboxColumn(): TableColumn {
        return this.columns.find(column => this.isSelectCheckboxColumn(column));
    }

    get toolsColumn(): TableColumn {
        return this.columns.find(column => this.isToolsColumn(column));
    }

    get normalColumns(): TableColumn[] {
        return this.columns.filter(column => !this.isDesignerToolsColumn(column) &&
            !this.isExpandColumn(column) &&
            !this.isSelectCheckboxColumn(column) &&
            !this.isToolsColumn(column));
    }

    isDesignerToolsColumn(column: TableColumn): boolean {
        return column instanceof DesignerToolsTableColumn;
    }

    isExpandColumn(column: TableColumn): boolean {
        return column instanceof ExpandTableColumn;
    }

    isSelectCheckboxColumn(column: TableColumn): boolean {
        return column instanceof SelectCheckboxTableColumn;
    }

    isToolsColumn(column: TableColumn): boolean {
        return column instanceof ToolsTableColumn;
    }

    getCustomColumnWidthsSettingsKey(): string {
        const parentLayoutName = this.getParentLayout()?.layoutName;
        if (StringUtil.isEmptyString(parentLayoutName) || StringUtil.isEmptyString(this.id)) {
            return null;
        }
        return  `${parentLayoutName}:${this.id}`;
    }

    hasCustomColumnWidths(): boolean {
        return LocalUserSettings.hasSetting(HeadingTableCellResizer.LOCAL_SETTINGS_KEY, this.getCustomColumnWidthsSettingsKey());
    }
}

// Apply mixins at runtime
MixinUtil.applyMixins(Table, [ BusyMixin ]);

// TypeScript interface declaration for mixin integration
export interface Table extends BusyMixinInterface {
}

ComponentTypes.registerComponentType("table", {
    constructor: Table.prototype.constructor
});
